/* global PasswordCredential */

import React, { useContext, useEffect, useRef, useState } from "react";
import { init as csInit, imageLoader } from "@cornerstonejs/core";
import { AnimatePresence, motion } from "framer-motion";
import * as dicomImageLoader from "@cornerstonejs/dicom-image-loader";
import "./style.css";
import AIAnnotatedImage from "./AIAnnotatedImage";
import AIDetailsButton from "./AIDetailsButton";
import BrandingSection from "./BrandingSection";
import registerWebImageLoader from "../StudyViewer/helpers/RegisterWebImageLoader";
import userContext from "../../context/users/UserContext";
import { encryptData } from "../../helpers/RSAUtility";
import NextStudiesIcon from "./SVGComponents/NextStudiesIcon";
import NextStudiesTable from "./NextStudiesTable";
import AIOutputVendorIcon from "./SVGComponents/RadilogistAnalytics";
import { API_BASE_URL } from "../../constants/Constants";
import { useSearchParams } from "react-router-dom";
import ReportButtonIcon from "./SVGComponents/ReportButtonIcon";
import { t } from "i18next";
import LowGizmo from "./SVGComponents/LowGizmo";
import MediumGizmo from "./SVGComponents/MediumGizmo";
import HighGizmo from "./SVGComponents/HighGizmo";
import NoAIOutputGizmo from "./SVGComponents/NoAIOutputGizmo";
import NonFrontalGizmo from "./SVGComponents/NonFrontalGizmo";
import CriticalGizmo from "./SVGComponents/CriticalGizmo";
import SuspectGizmo from "./SVGComponents/SuspectGizmo";
import ForeignBodyGizmo from "./SVGComponents/ForeignBodyGizmo";
import RadilogistAnalytics from "./SVGComponents/RadilogistAnalytics";
import RAIPerformanceResults from "./RAIPerformanceResults";
import AnalyticsPage from "./AnalyticsPage";
import NearMouseTooltip from "./helpers/NearMouseTooltip";

import { s } from "@kitware/vtk.js/macros2";
import SeriesStackScrollMouseWheelTool from "../StudyViewer/helpers/SeriesStackScrollMouseWheelTool";
import { MouseBindings } from "@cornerstonejs/tools/enums";
import ProductivityGainIcon from "./SVGComponents/ProductivityGainIcon";
import RAIIcon from "./SVGComponents/RAIIcon";
import RefreshButtonErrorScreen from "./SVGComponents/RefreshButtonErrorScreen";
import { forEach } from "underscore";
import { getEnv } from '../../helpers/EnvHelper';

// Default search DTO
export const initSearchDto = {
  data: {
    aiPredictionId: null,
  }
};

const setSeriesStackScrollConfig = (toolGroupManager, seriesScrollCallback) => {
  const seriesStackScrollConfig = toolGroupManager.getToolConfiguration(SeriesStackScrollMouseWheelTool.toolName);
  const seriesStackScrollMergedConfig = { ...seriesStackScrollConfig, seriesScrollCallback };
  toolGroupManager.setToolConfiguration(SeriesStackScrollMouseWheelTool.toolName, seriesStackScrollMergedConfig, true);
}

let cornerstoneInitialized = false;

// Resizable panel container
const ResizablePanel = ({ activePanel, children }) => (
  <motion.div
    animate={{ height: activePanel ? 240 : 0, opacity: activePanel ? 1 : 0 }}
    transition={{ duration: 0.5 }}
    style={{
      overflow: "hidden",
      width: "350px",
      pointerEvents: activePanel ? "auto" : "none",
    }}
  >
    <div className="gizmo-container" style={{
      background: '#040811',
      borderTopLeftRadius: 10,
      borderTopRightRadius: 10,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}>
      {children}
    </div>
  </motion.div>
);

function initializeCornerstone() {
  csInit();
  if (!cornerstoneInitialized) {
    dicomImageLoader.init({
      startWebWorkersOnDemand: false,
      beforeSend: function (xhr) {
        xhr.withCredentials = true;
      },
    });
    imageLoader.registerImageLoader("dicomweb", dicomImageLoader.wadouri.loadImage);
    // wadors: per-frame loader for un-split multi-frame DICOMs served by the
    // backend WADO-RS frame endpoint. Single-frame and pre-split multi-frame
    // continue to load via `wadouri:` / `dicomweb:` above.
    imageLoader.registerImageLoader("wadors", dicomImageLoader.wadors.loadImage);
    registerWebImageLoader();


    cornerstoneInitialized = true;
  }
}

function measureAndResize({ activePanel, containerRef, containerRef1, containerRef2, postResizeIfChanged }) {
  const heightE = containerRef?.current;
  const widthE = containerRef?.current;
  const heightEl = containerRef1?.current;
  const widthEl = containerRef2?.current;

  // Prevent crashing if refs aren't attached yet
  if (!heightE || !widthE || !heightEl || !widthEl) return;

  const heightRect = heightE.getBoundingClientRect();
  const widthRect = widthEl.getBoundingClientRect();

  const styleH = window.getComputedStyle(heightE);
  const styleW = window.getComputedStyle(widthE);

  if (activePanel === null) {
    if (!heightEl || !widthEl) return;

    const heightRect = heightEl.getBoundingClientRect();
    const widthRect = widthEl.getBoundingClientRect();

    // const styleH1 = window.getComputedStyle(heightEl);
    // const styleW1 = window.getComputedStyle(widthEl);

    const paddingY = parseFloat(styleH.paddingTop) + parseFloat(styleH.paddingBottom);
    const paddingX = parseFloat(styleW.paddingLeft) + parseFloat(styleW.paddingRight);

    const totalHeight = Math.ceil(heightRect.height + paddingY);
    const totalWidth = Math.ceil(widthRect.width + paddingX + 20);
    const finalHeight = activePanel === null ? Math.max(totalHeight, 80) : totalHeight+1;
    //+1 to prevent jumping when opening panel
    postResizeIfChanged(totalWidth, finalHeight);

  }
  else {
    if (!heightE || !widthE) return;


    const paddingY = parseFloat(styleH.paddingTop) + parseFloat(styleH.paddingBottom);
    const paddingX = parseFloat(styleW.paddingLeft) + parseFloat(styleW.paddingRight) + parseFloat(styleW.borderLeftWidth) + parseFloat(styleW.borderRightWidth);

    const totalHeight = Math.ceil(heightRect.height + paddingY);
    const totalWidth = Math.ceil(widthRect.width + paddingX + 20);
    const finalHeight = activePanel === null ? Math.max(totalHeight, 80) : totalHeight+1;
    //+1 to prevent jumping when opening panel
    postResizeIfChanged(totalWidth, finalHeight);
  }


}


const GizmoWidget = () => {
  const { signin,signin2 } = useContext(userContext);
  const [showFindings, setShowFindings] = useState(false);
  const [colapseView, setColapseView] = useState(false);
  const [somePredictionId, setSomePredictionId] = useState('');
  const [data, setData] = useState(null);
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState(null);
  const [aiEmegencyData, setAIEmegencyData] = useState(null);
  const [activePanel, setActivePanel] = useState(null);
  const [pendingPanel, setPendingPanel] = useState(null);
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(true);
  // ✅ DEFAULT INITIAL VALUE
  const [aiEmegencyDetails, setAIEmegencyDetails] = useState({
    icon: NoAIOutputGizmo,
    label: t("study-list.filters.no-ai-output"),
  });

  const [nextStudies, setNextStudies] = useState([]);
  const [highlightNextStudies, setHighlightNextStudies] = useState(null);
  const [lastOpenedStudyId, setLastOpenedStudyId] = useState(null);

  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [showNextStudies, setShowNextStudies] = useState(false);
  const [showPasswordScreen, setShowPasswordScreen] = useState(true);
  const [findingsAvailable, setfindingsAvailable] = useState(false);
  const [userName, setUserName] = useState(null);
  const [studyId, setStudyId] = useState(null);
  const [firmId, setFirmId] = useState(null);
  const [modalityName, setModalityName] = useState(null);
  const [dicomUrl, setDicomUrl] = useState(null);
  const [heatmapUrl, setHeatmapUrl] = useState(null);
  const [outlineUrl, setOutlineUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [appState, setAppState] = useState("loading");
  const [findings, setFindings] = useState([{
    "prediction": 0,
    "pathologyId": -1,
    "value": "",
    "label": ""
  }]);
  const [inputPassword, setInputPassword] = useState('');
  const containerRef = useRef(null);
  const containerRef1 = useRef(null);
  const containerRef2 = useRef(null);
  const skipAnimationRef = useRef(false);

  const [isAugDemo, setIsAugDemo] = useState(false);
    const [augDemoStudyId, setAugDemoStudyId] = useState("");
  const [augDemoFirmId, setAugDemoFirmId] = useState("");
  const [augDemoUsername, setAugDemoUsername] = useState("");
  const [augDemoPassword, setAugDemoPassword] = useState("");

  const lastSize = useRef({ width: 0, height: 0 });
  const resizeTimeout = useRef(null);
  const themeName = 'dark';

  const getIconAndLabel = () => {
    if (!aiEmegencyData) return;

    const { aiEmergencyMax, aiProcessed, reportDoneStatus } = aiEmegencyData;

    const conditions = [
      {
        condition: (aiEmergencyMax === -1 && aiProcessed === 0) || (reportDoneStatus !== 2 && aiProcessed === 0) || aiProcessed === 3,
        result: { icon: NoAIOutputGizmo, label: t("study-list.filters.no-ai-output"), findingsAvailable: false },
      },
      { condition: aiEmergencyMax === -3, result: { icon: NonFrontalGizmo, label: t("study-list.filters.non-frontal"), findingsAvailable: false } },
      { condition: aiEmergencyMax === 0 || aiEmergencyMax === 1, result: { icon: LowGizmo, label: t("study-list.filters.low"), findingsAvailable: false } },
      { condition: aiEmergencyMax === 2, result: { icon: MediumGizmo, label: t("study-list.filters.medium"), findingsAvailable: true } },
      { condition: aiEmergencyMax === 3, result: { icon: HighGizmo, label: t("study-list.filters.high"), findingsAvailable: true } },
      { condition: aiEmergencyMax === 4, result: { icon: CriticalGizmo, label: t("study-list.filters.critical"), findingsAvailable: true } },
      { condition: aiEmergencyMax === 7, result: { icon: SuspectGizmo, label: t("study-list.filters.suspect"), findingsAvailable: true } },
      { condition: aiEmergencyMax === 6, result: { icon: ForeignBodyGizmo, label: t("study-list.filters.fo-only"), findingsAvailable: true } },
    ];

    let matched = false;
    // console.log("AI Output:",data.aiOutput, "AI Vendor:", data.aiVendor);
    if (data.isChestXray) {
      for (const { condition, result } of conditions) {
        if (condition) {
          setAIEmegencyDetails(result);
          setfindingsAvailable(result.findingsAvailable);
          matched = true;
          break;
        }
      }
    }

    else if (data.aiOutput !== null && data.aiVendor !== null) {
      const resultNonXray = { icon: CriticalGizmo, label: t("study-list.filters.critical"), findingsAvailable: true }
      for (const { condition, result } of conditions) {
        if (condition) {
          setAIEmegencyDetails(result);
          setfindingsAvailable(resultNonXray.findingsAvailable);
          matched = true;
          break;
        }
      }
    }
    else if (data.aiOutput === null && data.aiVendor !== null) {
      const resultNonXray = { icon: LowGizmo, label: t("study-list.filters.low"), findingsAvailable: false };
        for (const { condition, result } of conditions) {
        if (condition) {
          setAIEmegencyDetails(result);
          setfindingsAvailable(resultNonXray.findingsAvailable);
          matched = true;
          break;
        }
      }
    }

    if (!matched) {
      setAIEmegencyDetails({ icon: NoAIOutputGizmo, label: t("study-list.filters.no-ai-output"), findingsAvailable: false });
    }
  };
  const getListForNextStudies = async (searchDto) => {
  try {
    const response = await fetch(`${API_BASE_URL}jwtServices/gizmo/get/next-studies`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(searchDto)
    });

    // 🔥 detect 403 explicitly
    if (response.status === 403) {
      return { error403: true };
    }
    const result = await response.json();
    return result.result === "SUCCESS" ? result.data : [];


  } catch {
    return [];
  }
};

  const loadNextStudies = async () => {
  try {
    let studies = await getListForNextStudies(initSearchDto);

        if (studies?.error403) {
      console.warn("403 in polling → renewing session...");
      window.parent.postMessage({ type: "get-credentials", userName }, "*"); // you are already passing these above
      return;
    }
    // ⭐ SORT BY aiEmergencyMax DESC (valid values 1–4 only)
    studies = studies.sort((a, b) => {
      const aVal = (a.aiEmergencyMax >= 1 && a.aiEmergencyMax <= 4) ? a.aiEmergencyMax : 0;
      const bVal = (b.aiEmergencyMax >= 1 && b.aiEmergencyMax <= 4) ? b.aiEmergencyMax : 0;
      return bVal - aVal;  // DESC
    });

    setNextStudies(studies);

    // -----------------------------------------
    // EXISTING FILTERING BASED ON lastOpenedStudyId
    // -----------------------------------------
    let newStudies = studies;
    if (lastOpenedStudyId) {
      const index = studies.findIndex(s => s.studyId === lastOpenedStudyId);
      if (index !== -1) {
        newStudies = studies.slice(0, index);
      }
    }

    // -----------------------------------------
    // MAX EMERGENCY CALCULATION
    // -----------------------------------------
    const maxEmergency = newStudies.reduce((max, s) => {
      const val = s.aiEmergencyMax;
      const valid = val >= 1 && val <= 4 ? val : 0;
      return Math.max(max, valid);
    }, 0);

    setHighlightNextStudies(maxEmergency);

  } catch (err) {
    console.warn("Next-studies failed, disabling icon:", err);
    setNextStudies([]);
    setHighlightNextStudies(0);
  }
};


  const doAuthAndLoad = async (userName, password) => {
    try {
      const authResponse = await signin(encryptData(userName), password);

        if (authResponse?.status !== 200) {

    const message = authResponse?.data?.message;

    // 🔒 1. USER LOCKED
    if (message === "LOCKED") {
        setErrorMessage("Augmento User Locked");
        setAppState("error");
        return;
    }

    // 🔢 2. ATTEMPTS REMAINING (2,3,4,5 or ONE_ATTEMPT_REMAINING)
    const isAttemptCount =
        message &&
        (message === "ONE_ATTEMPT_REMAINING" || !isNaN(Number(message)));

    if (isAttemptCount) {
        setAttemptsRemaining(message);
        setAppState("password");
        return;
    }

    // ❌ 3. ANY OTHER FAILURE → generic backend login error
    setErrorMessage("Failed to login to Augmento");
    setAppState("error");
    return;
}


      document.cookie = `token=${authResponse.data.data.token}; Secure; `;
      document.cookie = `refresh-token=${authResponse.data.data.refreshToken}; Secure; `;

      initializeCornerstone();
      // await doRAIAuth();

      const params = new URLSearchParams();
      const studyInstanceUID = searchParams.get("study_instance_uid");
      if (studyInstanceUID) params.append("study_instance_uid", studyInstanceUID);
      const accessionNumber = searchParams.get("accession_number");
      if (accessionNumber) params.append("accession_number", accessionNumber);

      const fetchData = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}jwtServices/gizmo/get-data?${params.toString()}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          });
          const result = await response.json();
          if (result.errorMessage) {
            setErrorMessage(result.errorMessage);
            setAppState("error");
            return;
          }
          else {
            // console.log(result);
            setData(result);
            setStudyId(result.studyId);
            setFirmId(result.firmId);
            setModalityName(result.modalityName);
            setDicomUrl(`${API_BASE_URL}jwtServices/study/imageWithFileSize/get/${result.imageId}/${result.thumbnailStorageKey}`);
            if (result.heatmapStorageKey !== null) {
              setHeatmapUrl(`${API_BASE_URL}jwtServices/study/imageWithFileSize/get/${result.imageId}/${result.heatmapStorageKey}`);
            }
            if (result.outlineStorageKey !== null) {
              setOutlineUrl(`${API_BASE_URL}jwtServices/study/imageWithFileSize/get/${result.imageId}/${result.outlineStorageKey}`);
            }
            setAIEmegencyData({
              aiProcessed: result.aiProcessed,
              aiEmergencyMax: result.aiEmergencyMax,
              reportDoneStatus: result.reportDoneStatus,
            });


            console.log('result.aiOutput:', result.aiOutput);
            if(result.isChestXray) {
              if (result.studyPredictionList && result.studyPredictionList.length > 0) {
                setFindings(result.studyPredictionList.map(prediction => ({
                  prediction: prediction.probabilityDisplay,
                  pathologyId: prediction.pathologyId,
                  value: prediction.pathologyName,
                  label: prediction.pathologyName,
                })));
              }
            } else if (result.aiOutput) {
              const aiOutput = result.aiOutput.split(',');
              if(aiOutput.length > 0 && aiOutput[0] !== '') {
                forEach(aiOutput, (item) => {
                  const [pathologyName, prediction] = item.split('-');
                  setFindings(prevFindings => [
                    ...prevFindings,
                    {
                      prediction: prediction, // Default value, adjust as needed
                      pathologyId: 0,
                      value: pathologyName,
                      label: pathologyName,
                    }
                  ]);
                });
              }
            }
            if(result.augmentoDemoStudyJson){
                    try {
                      const parsed = JSON.parse(result.augmentoDemoStudyJson);
                      const authResponse = await signin2(encryptData(parsed.username),encryptData(parsed.password));

      if (authResponse?.status === 200) {
        setIsAugDemo(true);
        setAugDemoStudyId(parsed.studyId || "");
        setAugDemoFirmId(parsed.firmId || "");
        setAugDemoUsername(parsed.username || "");
      }
        


      } catch (e) {
        console.error("Failed to parse JSON or Authetication:", e);
      }
            }
          }
        } catch (error) {
          setErrorMessage("An error occurred while fetching the data.");
        }
      };



      await fetchData();
      await new Promise((res) => setTimeout(res, 50));
      loadNextStudies();

//       let newStudies = studies;
//       if (lastOpenedStudyId) {
//         const index = studies.findIndex(s => s.studyId === lastOpenedStudyId);
//         if (index !== -1) {
//           newStudies = studies.slice(0, index); // Only newer studies
//         }
//       }

//       const maxEmergency = newStudies.reduce((max, s) => {
//   const val = s.aiEmergencyMax;
//   const valid = val >= 1 && val <= 4 ? val : 0;
//   return Math.max(max, valid);
// }, 0);


//       setHighlightNextStudies(maxEmergency);
      setAppState("ready");

    } catch (err) {
      console.error(err);
      setErrorMessage("An unexpected error occurred.");
      setAppState("error");
    }
  };

  // const doRAIAuth = async () => {
  //   let authResult = 'SUCCESS';
  //   try {
  //     const response = await fetch(import.meta.env.VITE_RAI_BASE_URL + `api/rai-vimhub/auth/login`, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //         // 'Access-Control-Allow-Origin': '*',
  //         // 'Access-Control-Allow-Methods': '*',
  //         // 'Access-Control-Allow-Headers': '*',
  //       },
  //       body: JSON.stringify({
  //         // "augmento_token": import.meta.env.VITE_RAI_AUG_TOKEN,
  //       }),
  //       // mode: "cors", // Ensures CORS preflight is sent
  //     });
  //     const result = await response.json();
  //     const token = result['x-access-token'];
  //     if (!response.ok) {
  //       const errorResult = await response.json();
  //       //setErrorMessage(errorResult.errorMessage || "Authentication failed");
  //       setRaiAuthStatus({ result: 'FAILURE', errorMessage: errorResult.errorMessage || "Authentication failed" });
  //       return 'FAILURE';
  //     } else {
  //       localStorage.setItem("raiToken", token);
  //     }
  //   } catch (error) {
  //     //setErrorMessage("An error occurred while fetching the data.");
  //     setRaiAuthStatus({ result: 'FAILURE', errorMessage: "An error occurred while fetching the data." });
  //     return 'FAILURE';
  //   }
  //   setRaiAuthStatus({ result: authResult, errorMessage: "" });
  //   return authResult;
  // };

  useEffect(() => {
    if (!["password", "error", "loading"].includes(appState)) return;

    // Wait for layout to stabilize (especially for dynamic content like error messages)
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (
          containerRef.current &&
          containerRef1.current &&
          containerRef2.current
        ) {
          measureAndResize({
            activePanel,
            containerRef,
            containerRef1,
            containerRef2,
            postResizeIfChanged,
          });
        }
      });
    });

    return () => cancelAnimationFrame(rafId);
  }, [appState]);

  useEffect(() => {
    const userName = searchParams.get("userName");
    setUserName(userName);

    const isElectron = searchParams.get("isElectron");
    if (isElectron) {
      window.parent.postMessage({ type: "get-credentials", userName }, "*");
    } else {
      (async () => {
        try {
          const credential = await navigator.credentials.get({ password: true, mediation: "optional" });
          if (credential?.password) {
            setShowPasswordScreen(false);
            await doAuthAndLoad(userName, credential.password);
          } else {
            setAppState("password");
          }
        } catch (err) {
          setErrorMessage("Auto-login failed.");
          setAppState("error");
        }
      })();
    }

    const handleMessage = (event) => {
      if (event.data?.type === "credentials") {
        const { password } = event.data;
        if (password) {
          setShowPasswordScreen(false);
          doAuthAndLoad(userName, password);
        } else {
          setAppState("password");
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);


const processAndSetNextStudies = (studies) => {
  if (!Array.isArray(studies)) studies = [];

  // ⭐ Sort by Emergency Priority (1–4 DESC)
  studies = studies.sort((a, b) => {
    const aVal = a.aiEmergencyMax >= 1 && a.aiEmergencyMax <= 4 ? a.aiEmergencyMax : 0;
    const bVal = b.aiEmergencyMax >= 1 && b.aiEmergencyMax <= 4 ? b.aiEmergencyMax : 0;
    return bVal - aVal;
  });

  setNextStudies(studies);

  let newStudies = studies;
  if (lastOpenedStudyId) {
    const index = studies.findIndex(s => s.studyId === lastOpenedStudyId);
    if (index !== -1) newStudies = studies.slice(0, index);
  }

  const maxEmergency = newStudies.reduce((max, s) => {
    const val = s.aiEmergencyMax;
    const valid = val >= 1 && val <= 4 ? val : 0;
    return Math.max(max, valid);
  }, 0);

  setHighlightNextStudies(maxEmergency);
};

useEffect(() => {
  const fetchData = async () => {
    const searchDto =
      somePredictionId !== "All"
        ? {
            ...initSearchDto,
            data: {
              ...initSearchDto.data,
              aiPredictionId: somePredictionId,
            },
          }
        : initSearchDto;

    const studies = await getListForNextStudies(searchDto);

    // 🔥 403 check — re-authenticate silently
    if (studies?.error403) {
      console.warn("403 inside filter useEffect → re-authenticating...");
      window.parent.postMessage({ type: "get-credentials", userName }, "*");
      return;
    }

    // Normal processing
    processAndSetNextStudies(studies);
  };

  if (somePredictionId) fetchData();
}, [somePredictionId]);


//   useEffect(() => {
//     const fetchData = async () => {
//       // console.log(somePredictionId);
//       if (somePredictionId !== 'All') {
//         const searchDto = {
//           ...initSearchDto,
//           data: {
//             ...initSearchDto.data,
//             aiPredictionId: somePredictionId, // dynamically set
//           },
//         };
//         const studies = await getListForNextStudies(searchDto);
//         setNextStudies(studies);
//         let newStudies = studies;
//         if (lastOpenedStudyId) {
//           const index = studies.findIndex(s => s.studyId === lastOpenedStudyId);
//           if (index !== -1) {
//             newStudies = studies.slice(0, index); // Only newer studies
//           }
        

//         const maxEmergency = newStudies.reduce((max, s) => {
//   const val = s.aiEmergencyMax;
//   const valid = val >= 1 && val <= 4 ? val : 0;
//   return Math.max(max, valid);
// }, 0);

        
//         setHighlightNextStudies(maxEmergency);
//         }
//       }
//       else {
//         const studies = await getListForNextStudies(initSearchDto);
//         setNextStudies(studies);
//         let newStudies = studies;
//         if (lastOpenedStudyId) {
//           const index = studies.findIndex(s => s.studyId === lastOpenedStudyId);
//           if (index !== -1) {
//             newStudies = studies.slice(0, index); // Only newer studies
//           }

//         const maxEmergency = newStudies.reduce((max, s) => {
//   const val = s.aiEmergencyMax;
//   const valid = val >= 1 && val <= 4 ? val : 0;
//   return Math.max(max, valid);
// }, 0);


//         setHighlightNextStudies(maxEmergency);
        

//       }
//       }
//     };

//     if (somePredictionId) {
//       fetchData();
//     }
//   }, [somePredictionId]);

//   const pollNextStudies = async (somePredictionId) => {
//     try {
//       // console.log(somePredictionId);
//       const searchDto = somePredictionId !== 'All' && somePredictionId !== ''
//         ? {
//           ...initSearchDto,
//           data: {
//             ...initSearchDto.data,
//             aiPredictionId: somePredictionId,
//           },
//         }
//         : initSearchDto;
//       // console.log("searchDto : ", searchDto);
//       const studies = await getListForNextStudies(searchDto);
//       setNextStudies(studies);

//       let newStudies = studies;
//       // console.log("lastOpenedStudyId : ", lastOpenedStudyId);
//       if (lastOpenedStudyId) {
//         const index = studies.findIndex(s => s.studyId === lastOpenedStudyId);
//         if (index !== -1) {
//           newStudies = studies.slice(0, index); // Only newer studies
//         }
//       }

//       const maxEmergency = newStudies.reduce((max, s) => {
//   const val = s.aiEmergencyMax;
//   const valid = val >= 1 && val <= 4 ? val : 0;
//   return Math.max(max, valid);
// }, 0);


//       setHighlightNextStudies(maxEmergency);

//     } catch (error) {
//       setNextStudies([]);
//       console.error("Error fetching next studies:", error);
//       setHighlightNextStudies(0);
//     }
//   };

const pollNextStudies = async () => {
  try {
    const searchDto =
      somePredictionId !== "All" && somePredictionId !== ""
        ? {
            ...initSearchDto,
            data: {
              ...initSearchDto.data,
              aiPredictionId: somePredictionId,
            },
          }
        : initSearchDto;

    const studies = await getListForNextStudies(searchDto);

    if (studies?.error403) {
      console.warn("403 inside filter useEffect → re-authenticating...");
      window.parent.postMessage({ type: "get-credentials", userName }, "*");
      return;
    }

    processAndSetNextStudies(studies);

  } catch (error) {
    console.error("Error fetching next studies:", error);
    setNextStudies([]);
    setHighlightNextStudies(0);
  }
};


  const handleOpenStudyList = () => {
    if (nextStudies.length > 0) {
      setLastOpenedStudyId(nextStudies[0].studyId); // Latest study
    }
    console.log("length :", nextStudies.length);
    setHighlightNextStudies(0);
  };

  // useEffect(() => {
  //   console.log("highlightNextStudies : ", highlightNextStudies);
  // }, [highlightNextStudies]);

  // useEffect(() => {
  //   console.log("Updated lastOpenedStudyId:", lastOpenedStudyId);
  // }, [lastOpenedStudyId]);
  // Initial and interval polling
useEffect(() => {
  const interval = setInterval(() => {
    pollNextStudies();
  }, 60000);

  return () => clearInterval(interval);
}, [somePredictionId]);

  const handleOpenViewerWindow = async (studyId, firmId, userName, isReportClicked,isAugDemo,isProductivityClicked, chartMode) => {
    try {
      let response = null;
      if(isAugDemo){
        response = await fetch(`https://augmento-demo.deeptek.ai:8002/gizmo/generate-access-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ studyId, firmId, isReportClicked,isProductivityClicked,chartMode }),
      });
      }
      else{
        response = await fetch(`${API_BASE_URL}jwtServices/gizmo/generate-access-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ studyId, firmId, isReportClicked,isProductivityClicked,chartMode }),
      });
      }
      

      if (!response.ok) {
        const error = await response.text();
        console.error("Failed to generate access code:", error);
        alert("Unable to open viewer.");
        return;
      }

      const data = await response.json();
      const accessCode = data.accessCode;
      let viewerUrl = null;
      if(isAugDemo){
        viewerUrl = `https://augmento-demo.deeptek.ai/gizmo-viewer?accessCode=${encodeURIComponent(accessCode)}&userName=${encodeURIComponent(userName)}`;
      }
      else{
        // viewerUrl = `augmentoreport://open?studyId=${studyId}&firmId=${firmId}&userName=${userName}`;
        viewerUrl = `${window.location.origin}/gizmo-viewer?accessCode=${encodeURIComponent(accessCode)}&userName=${encodeURIComponent(userName)}`;
      }
      window.parent.postMessage({ type: "open-external", url: viewerUrl }, "*");
    } catch (err) {
      console.error("Error in handleOpenViewerWindow:", err);
      alert("Something went wrong while opening the viewer.");
    }
  };
  	
  const handleOpenViewerWindow2 = async (studyId, firmId, userName, isReportClicked,isAugDemo,isProductivityClicked, chartMode) => {
    try {
      console.log("studyId :", studyId);
      console.log("studfirmIdyId :", firmId);
      console.log("userName :", userName);

      let viewerUrl = `augmentoreport://open?studyId=${studyId}&firmId=${firmId}&userName=${userName}`;

      window.parent.postMessage({ type: "open-external", url: viewerUrl }, "*");
    } catch (err) {
      console.error("Error in handleOpenViewerWindow:", err);
      alert("Something went wrong while opening the viewer.");
    }
  };

  useEffect(() => {
    if (aiEmegencyData) getIconAndLabel();

  }, [aiEmegencyData]);

  useEffect(() => {
    // if (appState !== "loading") {
    window.parent.postMessage({ type: "iframe-ready" }, "*");
    // }
  }, [appState]);

  const postResizeIfChanged = (width, height) => {
    if (
      Math.abs(width - lastSize.current.width) > 1 ||
      Math.abs(height - lastSize.current.height) > 1
    ) {
      lastSize.current = { width, height };
      // In ResizeObserver callback:
      if (activePanel === null && !showPlaceholder) {

        setTimeout(() => {

          setShowPlaceholder(false);
        }, 30);   // Wait until bottom component is visually stable before resizing
        window.parent?.postMessage({ type: "resize", width, height }, "*");
        // Tweak delay as needed
      }
      else {
        window.parent?.postMessage({ type: "resize", width, height }, "*");
      }
    }
  };

  const handleSubmitPassword = async () => {
    const userName1 = userName;
    const password = encryptData(inputPassword);
    const isElectron = searchParams.get("isElectron");
    setAppState("loading");
    if (isElectron) {
      window.parent?.postMessage({ type: "set-password", userName1, password }, "*");
    } else {
      const cred = new PasswordCredential({
        id: userName1,
        password: password
      });

      await navigator.credentials.store(cred);
      doAuthAndLoad(userName1, password);
    }
  };


  const handleMoveToggle = () => {
    const newDragging = !isDragging;
    setIsDragging(newDragging);
    window.parent?.postMessage(
      { type: newDragging ? "start-drag-mode" : "stop-drag-mode" },
      "*"
    );
  };

  const switchPanel = (targetPanel) => {
    if (activePanel === targetPanel) {
      setShowPlaceholder(true);
      setActivePanel(null);
      skipAnimationRef.current = false;
    } else if (activePanel) {
      setShowPlaceholder(true);
      skipAnimationRef.current = true;
      setPendingPanel(targetPanel);

    } else {
      skipAnimationRef.current = false;
      setPendingPanel(targetPanel);
    }
  };

  useEffect(() => {
    if (!(data && aiEmegencyData)) return;

    // Two chained requestAnimationFrame calls ensures full layout completion
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (
          containerRef.current &&
          containerRef1.current &&
          containerRef2.current
        ) {
          measureAndResize({
            activePanel,
            containerRef,
            containerRef1,
            containerRef2,
            postResizeIfChanged,
          });
        }
      });
    });

    return () => cancelAnimationFrame(rafId);
  }, [data, aiEmegencyData, appState]);


  useEffect(() => {
    if (!pendingPanel) return;
    setActivePanel(pendingPanel); // this mounts the panel invisibly
    // Wait until next frame to ensure it's mounted
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Now it's rendered, measure
        measureAndResize({
          activePanel: pendingPanel,
          containerRef,
          containerRef1,
          containerRef2,
          postResizeIfChanged
        });
        // Wait a tick before animating
        setTimeout(() => {
          setPendingPanel(null); // clear staging state
        }, 20);
      });
    });
  }, [pendingPanel]);



  if (appState === "password") {
    return (
      <div
        id="gizmo-widget-root"
        ref={containerRef}
        style={{ display: "inline-block", padding: '15px 8px 8px 8px' }}
      >
        <div className="gizmo-container" ref={containerRef2}>
          <div
            ref={containerRef1}
            style={{
              width: '100%',
              height: '100%',
              marginBottom: '2px',
              background: '#040811',
            }}
          >
            <div
              ref={containerRef1}
              className="gizmo-container"
              style={{
                width: '350px',
                justifyContent: 'center',
                background:
                  activePanel === null && animationComplete === true
                    ? 'transparent'
                    : 'rgb(12, 26, 44)', // ✅ FIXED spelling
                borderBottomLeftRadius: 50,
                borderTopLeftRadius: activePanel === null ? 50 : 0,
                transition: 'border-top-left-radius 1s ease',

                // borderBottom: '0.3px solid rgba(245, 245, 245, 0.20)',
                // borderRight: '0.3px solid rgba(245, 245, 245, 0.20)',
                borderLeft: '0.3px solid rgba(245, 245, 245, 0.4)',
                borderBottomRightRadius: 10,


              }}
            >
              <div style={{
                width: '100%', height: '100%', background: '#040811', zIndex: 20,
                // boxShadow: '0px 0px 10px rgba(255, 255, 255, 0.50)',
                borderRadius: '10px',
                border: '0.30px rgba(245, 245, 245, 0.20) solid',
                boxShadow: `
                  4px 0 7px rgba(255, 255, 255, 0.25),   /* right */
                -4px 0 7px rgba(255, 255, 255, 0.25),   /* left */
                  0 4px 7px rgba(255, 255, 255, 0.25)    /* bottom */
                `

              }} >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '350px',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    // paddingTop: '10px' // 👈 this pushes SVG to the right
                    height:'22px'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      color: 'white',
                      fontSize: 10,
                      fontFamily: 'Open Sans',
                      fontWeight: '700',
                      wordWrap: 'break-word',
                      paddingLeft: '10px',
                      marginTop: '3px',
                    }}
                  >
                    Username: {userName}
                  </div>

                  <div style={{ paddingRight: '13px' }}> {/* 👈 optional spacing from right edge */}
                    <svg width="60" height="14" viewBox="0 0 60 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M57.5181 11.4746H58.0553V12.4953L58.8074 11.4746H59.452L58.6999 12.4953L59.6132 13.9995H58.9685L58.3776 12.9788L58.0553 13.3549V13.9995H57.5181V11.4746Z" fill="#F58220" />
                      <path d="M41.7241 11.4746H42.4225C43.1746 11.4746 43.6581 11.8507 43.6581 12.7102C43.6581 13.5697 43.1746 13.9458 42.4225 13.9458H41.6704L41.7241 11.4746ZM42.4225 13.516C42.8523 13.516 43.1209 13.3011 43.1209 12.7102C43.1209 12.1193 42.7986 11.9044 42.4225 11.9044H42.3151V13.4623L42.4225 13.516Z" fill="#F5F5F5" />
                      <path d="M44.625 11.4746H46.1829V11.9581H45.1622V12.4416H46.0218V12.9251H45.1622V13.4623H46.1829V13.9458H44.5713L44.625 11.4746Z" fill="#F5F5F5" />
                      <path d="M47.2034 11.4746H48.7613V11.9581H47.7406V12.4416H48.6001V12.9251H47.7406V13.4623H48.7613V13.9458H47.1497L47.2034 11.4746Z" fill="#F5F5F5" />
                      <path d="M49.7283 11.4746H50.5878C51.125 11.4746 51.5548 11.6358 51.5548 12.2804C51.5548 12.9251 51.0713 13.0863 50.5878 13.0863H50.2655V13.9458H49.7283V11.4746ZM50.5878 12.7102C50.9101 12.7102 51.0176 12.549 51.0176 12.2804C51.0176 12.0118 50.8564 11.9044 50.5341 11.9044H50.2655V12.6565L50.5878 12.7102Z" fill="#F5F5F5" />
                      <path d="M52.8981 11.9581H52.2534V11.4746H54.1874V11.9581H53.5427V13.9458H53.0055L52.8981 11.9581Z" fill="#F58220" />
                      <path d="M54.9929 11.4746H56.5509V11.9581H55.5301V12.4416H56.3897V12.9251H55.5301V13.4623H56.5509V13.9458H54.9392L54.9929 11.4746Z" fill="#F58220" />
                      <path d="M15.6154 5.78132C15.6154 5.7276 15.6154 5.67388 15.6154 5.62015C15.6691 4.76061 15.7766 3.90107 16.4212 3.25641C17.0121 2.66547 17.9254 2.45059 18.7312 2.45059C19.5371 2.39687 20.3966 2.50431 21.2024 2.50431L21.2561 7.87645C21.2561 8.78972 21.2561 9.7567 20.7189 10.5088C20.2354 11.1535 19.5371 11.5295 18.7312 11.5832C17.9791 11.637 17.227 11.5295 16.5824 11.2072C16.5287 11.2072 16.4749 11.1535 16.4749 11.1535C16.4749 11.1535 16.5287 11.0997 16.5287 11.046L16.7435 10.8311L17.1733 10.4014C17.227 10.3476 17.227 10.3476 17.2808 10.3476C17.3345 10.2939 17.4419 10.2939 17.4419 10.3476C17.818 10.4551 18.194 10.5088 18.5701 10.4014C18.9461 10.2939 19.2685 10.1328 19.4833 9.86415C19.6982 9.43438 19.8057 8.95088 19.8057 8.46739C19.0536 9.11205 17.9254 9.32693 17.0659 8.89716C16.5287 8.57483 16.0989 8.09134 15.9377 7.5004C15.7228 6.90947 15.5617 6.31853 15.6154 5.78132ZM17.0121 5.94248C16.9584 6.4797 17.1196 7.01691 17.3882 7.44668C17.6031 7.82273 18.0329 7.9839 18.4626 7.9839C18.9998 8.03762 19.5371 7.66157 19.7519 7.17808V4.43828C19.5371 3.95479 19.0536 3.68618 18.4626 3.68618C18.0329 3.68618 17.6568 3.84735 17.3882 4.2234C17.1196 4.76061 17.0121 5.35155 17.0121 5.94248Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10" />
                      <path d="M45.0546 2.44922C46.3976 2.44922 47.0423 3.25504 47.096 4.75924V9.00323H45.6455V4.86668C45.6993 4.54435 45.5918 4.22203 45.3769 3.95342C45.162 3.73853 44.8397 3.63109 44.5174 3.63109C43.9802 3.63109 43.443 3.95342 43.2281 4.43691V9.00323H41.7776L41.8313 2.50294C41.8313 2.50294 44.2488 2.44922 45.0546 2.44922Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10" />
                      <path d="M24.6943 2.55795L24.7481 3.20261C25.2316 2.66539 25.8762 2.39678 26.6283 2.39678C27.4879 2.39678 28.0788 2.71911 28.4011 3.41749C28.8309 2.77283 29.583 2.39678 30.3888 2.39678C30.9798 2.34306 31.517 2.55795 31.9467 2.98772C32.3228 3.47121 32.484 4.11587 32.484 4.70681V8.89708H31.0335V4.81425C31.0335 4.49192 30.926 4.16959 30.7111 3.95471C30.4425 3.73982 30.1739 3.63238 29.7979 3.63238C29.4756 3.63238 29.2069 3.6861 28.9921 3.90099C28.7772 4.06215 28.616 4.33076 28.5623 4.59936V8.9508H27.1655V4.76053C27.2193 4.22331 26.7895 3.6861 26.2523 3.63238C26.1985 3.63238 26.0911 3.63238 26.0374 3.63238C25.5002 3.57866 25.0167 3.90098 24.8018 4.33076V8.9508H23.405V2.55795H24.6943Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10" />
                      <path d="M37.3724 9.1116C36.5665 9.16532 35.7607 8.84299 35.1698 8.25206C34.5788 7.6074 34.2565 6.80158 34.3102 5.94204V5.72715C34.3102 5.13621 34.4177 4.54528 34.6863 4.00806C34.9012 3.52457 35.2772 3.0948 35.707 2.82619C36.1368 2.50386 36.674 2.39642 37.2112 2.39642C38.017 2.3427 38.7154 2.66503 39.2526 3.25596C39.7898 3.90062 40.0584 4.76016 40.0047 5.61971V6.21064H35.7607C35.7607 6.69414 35.9219 7.12391 36.2979 7.44624C36.6203 7.76856 37.05 7.92973 37.4798 7.87601C38.1245 7.87601 38.7154 7.55368 39.0914 7.07019L39.8973 7.82229C39.6287 8.19834 39.2526 8.52066 38.8766 8.73555C38.3393 9.00416 37.8559 9.1116 37.3724 9.1116ZM37.1575 3.57829C36.7814 3.57829 36.4591 3.73946 36.1905 4.00806C35.9219 4.33039 35.7607 4.76016 35.7607 5.13621H38.5005V5.02877C38.5005 4.599 38.3931 4.22295 38.1245 3.95434C37.8559 3.68574 37.5335 3.57829 37.1575 3.57829Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10" />
                      <path d="M51.0715 1V2.55792H52.1997V3.63235H51.0715V7.23169C51.0715 7.39285 51.1253 7.60774 51.2327 7.7689C51.3939 7.87634 51.555 7.93006 51.7699 7.93006C51.9311 7.93006 52.0922 7.87634 52.3071 7.87634V9.00449C51.9848 9.05821 51.6625 9.16566 51.3401 9.16566C50.212 9.16566 49.6748 8.57472 49.6748 7.33913V3.63235H48.6003V2.55792H49.6748V1H51.0715Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10" />
                      <path d="M53.6501 5.72812C53.6501 5.13718 53.7575 4.54624 54.0262 4.00903C54.241 3.52554 54.6171 3.09577 55.0469 2.88088C55.5304 2.61227 56.0676 2.45111 56.6048 2.45111C57.4106 2.39739 58.1627 2.71972 58.6999 3.31065C59.2371 3.90159 59.6132 4.70741 59.6132 5.51323V5.83556C59.6132 6.4265 59.5057 6.96371 59.2371 7.55465C59.0222 8.03814 58.6462 8.46791 58.2164 8.6828C57.7329 8.9514 57.1957 9.11257 56.6585 9.11257C55.8527 9.16629 55.0469 8.79024 54.4559 8.1993C53.9187 7.55465 53.5964 6.6951 53.6501 5.78184V5.72812ZM55.1006 5.83556C55.0469 6.37277 55.208 6.90999 55.5304 7.39348C56.0138 8.03814 56.9271 8.14558 57.518 7.71581C57.6255 7.66209 57.6792 7.55465 57.8404 7.39348C58.1627 6.90999 58.2701 6.31905 58.2701 5.72812C58.3239 5.1909 58.1627 4.59997 57.8404 4.17019C57.4106 3.52554 56.4973 3.41809 55.8527 3.84787C55.7452 3.90159 55.6378 4.00903 55.5304 4.17019C55.208 4.65369 55.0469 5.24462 55.1006 5.83556Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10" />
                      <path d="M12.4457 8.62907C12.0159 9.16629 11.3176 9.4349 10.6192 9.38118C10.0282 9.4349 9.43731 9.22001 9.00754 8.79024C8.63149 8.25302 8.41661 7.66209 8.47033 7.01743V2.77344H9.92081V6.90999C9.92081 7.71581 10.2431 8.14558 10.9415 8.14558C11.6399 8.14558 12.1234 7.87697 12.3383 7.39348V2.77344H13.7887V9.16629H12.392L12.4457 8.62907Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10" />
                      <path d="M5.1934 9.05887C5.13968 8.84398 5.03224 8.6291 5.03224 8.41421C4.60247 8.8977 3.95781 9.16631 3.31315 9.16631C2.72222 9.22003 2.13128 9.00515 1.70151 8.6291C1.21802 8.25305 1.00313 7.71583 1.00313 7.17862C0.949409 6.58768 1.27174 5.94302 1.80895 5.56697C2.45361 5.1372 3.25943 4.92232 4.06525 5.02976H4.97852V4.54627C4.97852 4.22394 4.9248 3.95533 4.65619 3.74045C4.4413 3.47184 4.11897 3.3644 3.79665 3.41812C3.47432 3.41812 3.20571 3.47184 2.9371 3.68672C2.77594 3.79417 2.61477 4.06277 2.61477 4.27766H1.16429C1.16429 3.90161 1.32546 3.52556 1.54034 3.25695C1.80895 2.93462 2.13128 2.66602 2.50733 2.50485C2.9371 2.39741 3.4206 2.23625 3.90409 2.28997C4.54875 2.23625 5.24712 2.50485 5.73062 2.8809C6.21411 3.31067 6.48272 3.90161 6.429 4.54627V7.55467C6.37527 8.03816 6.48272 8.52165 6.6976 9.00515V9.11259L5.1934 9.05887ZM3.58176 7.98444C3.90409 7.98444 4.1727 7.93072 4.38758 7.76955C4.65619 7.60839 4.81735 7.44722 4.97852 7.17862V5.8893H4.1727C3.6892 5.83558 3.31315 5.99675 2.88338 6.21163C2.61477 6.3728 2.45361 6.69512 2.45361 7.01745C2.45361 7.28606 2.50733 7.55467 2.77594 7.71583C2.99082 7.877 3.25943 7.98444 3.58176 7.98444Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10" />
                    </svg></div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', paddingBottom: '5px', gap: '8px', height: '16px' }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      color: 'white',
                      fontSize: 10,
                      fontFamily: 'Open Sans',
                      fontWeight: '700',
                      wordWrap: 'break-word',
                      paddingLeft: '10px',
                      height: '16px'
                    }}
                  >
                    Password:
                  </div>
                  <input
                    type="password"
                    placeholder="Enter password"
                    onChange={(e) => setInputPassword(e.target.value)}
                    style={{
                      width: '132px',
                      height: '16px',
                      borderRadius: 5,
                      color: 'white',
                      border: '1px solid #F5F5F5',
                      paddingLeft: 10,
                      fontSize: 10,
                      fontFamily: 'Open Sans',
                      background: '#040811',
                    }}
                  />
                  <div style={{ height: '16px', paddingRight: '10px' }} onClick={handleSubmitPassword}>
                    <svg width="73" height="22" viewBox="0 0 73 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="73" height="22" rx="5" fill="#007BDA" />
                      <path d="M21.7539 12.6211C21.7539 13.1289 21.6309 13.5703 21.3848 13.9453C21.1387 14.3203 20.7793 14.6094 20.3066 14.8125C19.8379 15.0156 19.2676 15.1172 18.5957 15.1172C18.2988 15.1172 18.0078 15.0977 17.7227 15.0586C17.4414 15.0195 17.1699 14.9629 16.9082 14.8887C16.6504 14.8105 16.4043 14.7148 16.1699 14.6016V12.9141C16.5762 13.0938 16.998 13.2559 17.4355 13.4004C17.873 13.5449 18.3066 13.6172 18.7363 13.6172C19.0332 13.6172 19.2715 13.5781 19.4512 13.5C19.6348 13.4219 19.7676 13.3145 19.8496 13.1777C19.9316 13.041 19.9727 12.8848 19.9727 12.709C19.9727 12.4941 19.9004 12.3105 19.7559 12.1582C19.6113 12.0059 19.4121 11.8633 19.1582 11.7305C18.9082 11.5977 18.625 11.4551 18.3086 11.3027C18.1094 11.209 17.8926 11.0957 17.6582 10.9629C17.4238 10.8262 17.2012 10.6602 16.9902 10.4648C16.7793 10.2695 16.6055 10.0332 16.4688 9.75586C16.3359 9.47461 16.2695 9.13867 16.2695 8.74805C16.2695 8.23633 16.3867 7.79883 16.6211 7.43555C16.8555 7.07227 17.1895 6.79492 17.623 6.60352C18.0605 6.4082 18.5762 6.31055 19.1699 6.31055C19.6152 6.31055 20.0391 6.36328 20.4414 6.46875C20.8477 6.57031 21.2715 6.71875 21.7129 6.91406L21.127 8.32617C20.7324 8.16602 20.3789 8.04297 20.0664 7.95703C19.7539 7.86719 19.4355 7.82227 19.1113 7.82227C18.8848 7.82227 18.6914 7.85938 18.5312 7.93359C18.3711 8.00391 18.25 8.10547 18.168 8.23828C18.0859 8.36719 18.0449 8.51758 18.0449 8.68945C18.0449 8.89258 18.1035 9.06445 18.2207 9.20508C18.3418 9.3418 18.5215 9.47461 18.7598 9.60352C19.002 9.73242 19.3027 9.88281 19.6621 10.0547C20.0996 10.2617 20.4727 10.4785 20.7812 10.7051C21.0938 10.9277 21.334 11.1914 21.502 11.4961C21.6699 11.7969 21.7539 12.1719 21.7539 12.6211ZM29.1777 8.44922V15H27.8066L27.5664 14.1621H27.4727C27.332 14.3848 27.1562 14.5664 26.9453 14.707C26.7344 14.8477 26.502 14.9512 26.248 15.0176C25.9941 15.084 25.7305 15.1172 25.457 15.1172C24.9883 15.1172 24.5801 15.0352 24.2324 14.8711C23.8848 14.7031 23.6133 14.4434 23.418 14.0918C23.2266 13.7402 23.1309 13.2832 23.1309 12.7207V8.44922H24.918V12.2754C24.918 12.7441 25.002 13.0977 25.1699 13.3359C25.3379 13.5742 25.6055 13.6934 25.9727 13.6934C26.3359 13.6934 26.6211 13.6113 26.8281 13.4473C27.0352 13.2793 27.1797 13.0352 27.2617 12.7148C27.3477 12.3906 27.3906 11.9961 27.3906 11.5312V8.44922H29.1777ZM32.8398 5.88281V8.00391C32.8398 8.25 32.832 8.49414 32.8164 8.73633C32.8047 8.97852 32.7891 9.16602 32.7695 9.29883H32.8398C33.0117 9.0293 33.2461 8.80078 33.543 8.61328C33.8398 8.42188 34.2246 8.32617 34.6973 8.32617C35.4316 8.32617 36.0273 8.61328 36.4844 9.1875C36.9414 9.76172 37.1699 10.6035 37.1699 11.7129C37.1699 12.459 37.0645 13.0859 36.8535 13.5938C36.6426 14.0977 36.3477 14.4785 35.9688 14.7363C35.5898 14.9902 35.1504 15.1172 34.6504 15.1172C34.1699 15.1172 33.791 15.0312 33.5137 14.8594C33.2363 14.6875 33.0117 14.4941 32.8398 14.2793H32.7168L32.418 15H31.0527V5.88281H32.8398ZM34.123 9.75586C33.8105 9.75586 33.5625 9.82031 33.3789 9.94922C33.1953 10.0781 33.0605 10.2715 32.9746 10.5293C32.8926 10.7871 32.8477 11.1133 32.8398 11.5078V11.7012C32.8398 12.3379 32.9336 12.8262 33.1211 13.166C33.3086 13.502 33.6504 13.6699 34.1465 13.6699C34.5137 13.6699 34.8047 13.5 35.0195 13.1602C35.2383 12.8203 35.3477 12.3301 35.3477 11.6895C35.3477 11.0488 35.2383 10.5664 35.0195 10.2422C34.8008 9.91797 34.502 9.75586 34.123 9.75586ZM46.3398 8.32617C47.082 8.32617 47.6426 8.51758 48.0215 8.90039C48.4043 9.2793 48.5957 9.88867 48.5957 10.7285V15H46.8027V11.1738C46.8027 10.7051 46.7227 10.3516 46.5625 10.1133C46.4023 9.875 46.1543 9.75586 45.8184 9.75586C45.3457 9.75586 45.0098 9.92578 44.8105 10.2656C44.6113 10.6016 44.5117 11.084 44.5117 11.7129V15H42.7246V11.1738C42.7246 10.8613 42.6895 10.5996 42.6191 10.3887C42.5488 10.1777 42.4414 10.0195 42.2969 9.91406C42.1523 9.80859 41.9668 9.75586 41.7402 9.75586C41.4082 9.75586 41.1465 9.83984 40.9551 10.0078C40.7676 10.1719 40.6328 10.416 40.5508 10.7402C40.4727 11.0605 40.4336 11.4531 40.4336 11.918V15H38.6465V8.44922H40.0117L40.252 9.28711H40.3516C40.4844 9.06055 40.6504 8.87695 40.8496 8.73633C41.0527 8.5957 41.2754 8.49219 41.5176 8.42578C41.7598 8.35938 42.0059 8.32617 42.2559 8.32617C42.7363 8.32617 43.1426 8.4043 43.4746 8.56055C43.8105 8.7168 44.0684 8.95898 44.248 9.28711H44.4062C44.6016 8.95117 44.877 8.70703 45.2324 8.55469C45.5918 8.40234 45.9609 8.32617 46.3398 8.32617ZM52.2227 8.44922V15H50.4355V8.44922H52.2227ZM51.332 5.88281C51.5977 5.88281 51.8262 5.94531 52.0176 6.07031C52.209 6.19141 52.3047 6.41992 52.3047 6.75586C52.3047 7.08789 52.209 7.31836 52.0176 7.44727C51.8262 7.57227 51.5977 7.63477 51.332 7.63477C51.0625 7.63477 50.832 7.57227 50.6406 7.44727C50.4531 7.31836 50.3594 7.08789 50.3594 6.75586C50.3594 6.41992 50.4531 6.19141 50.6406 6.07031C50.832 5.94531 51.0625 5.88281 51.332 5.88281ZM56.8633 13.6934C57.0586 13.6934 57.248 13.6738 57.4316 13.6348C57.6191 13.5957 57.8047 13.5469 57.9883 13.4883V14.8184C57.7969 14.9043 57.5586 14.9746 57.2734 15.0293C56.9922 15.0879 56.6836 15.1172 56.3477 15.1172C55.957 15.1172 55.6055 15.0547 55.293 14.9297C54.9844 14.8008 54.7402 14.5801 54.5605 14.2676C54.3848 13.9512 54.2969 13.5117 54.2969 12.9492V9.79102H53.4414V9.03516L54.4258 8.4375L54.9414 7.05469H56.084V8.44922H57.918V9.79102H56.084V12.9492C56.084 13.1992 56.1543 13.3867 56.2949 13.5117C56.4395 13.6328 56.6289 13.6934 56.8633 13.6934Z" fill="white" />
                    </svg>


                  </div>

                </div>
                {attemptsRemaining && (
                    <div className="attempt-counter" 
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      color: 'red',
                      fontSize: 10,
                      fontFamily: 'Open Sans',
                      fontWeight: '700',
                      wordWrap: 'break-word',
                      paddingLeft: '10px',
                      height: '16px'
                    }}>
                        {attemptsRemaining === "ONE_ATTEMPT_REMAINING"
                            ? "Only 1 attempt remaining!"
                            : `${attemptsRemaining} attempts remaining`}
                    </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }


  if (appState === "error") {
    return (<div
      id="gizmo-widget-root"
      ref={containerRef}
      style={{ display: "inline-block", padding: '15px 8px 8px 8px' }}
    >
      <div className="gizmo-container" ref={containerRef2}>
        <div
          ref={containerRef1}
          style={{
            width: '100%',
            height: '100%',
            marginBottom: '2px',
            background: '#040811',
          }}
        >
          <div
            ref={containerRef1}
            className="gizmo-container"
            style={{
              width: '350px',
              justifyContent: 'center',
              background:
                activePanel === null && animationComplete === true
                  ? 'transparent'
                  : 'rgb(12, 26, 44)', // ✅ FIXED spelling
              borderBottomLeftRadius: 50,
              borderTopLeftRadius: activePanel === null ? 50 : 0,
              transition: 'border-top-left-radius 1s ease',

              // borderBottom: '0.3px solid rgba(245, 245, 245, 0.20)',
              // borderRight: '0.3px solid rgba(245, 245, 245, 0.20)',
              borderLeft: '0.3px solid rgba(245, 245, 245, 0.4)',
              borderBottomRightRadius: 10,


            }}
          >
            <div style={{
              width: '100%',  background: '#040811', zIndex: 20,
              height: '60px',
              // boxShadow: '0px 0px 10px rgba(255, 255, 255, 0.50)',
              borderRadius: '10px',
              border: '0.30px rgba(245, 245, 245, 0.20) solid',
              boxShadow: `
                  4px 0 7px rgba(255, 255, 255, 0.25),   /* right */
                -4px 0 7px rgba(255, 255, 255, 0.25),   /* left */
                  0 4px 7px rgba(255, 255, 255, 0.25)    /* bottom */
                `,
              display:'flex',alignItems:'center'
            }} >
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' ,width:'350px',alignItems:'center'}} >
                <div style={{height:'36px'}}>
                <NearMouseTooltip content={t(errorMessage)}>
                <div style={{display: 'flex',alignItems:'center',marginLeft:'19px'}}>
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 2.75C26.4223 2.75 33.25 9.57766 33.25 18C33.25 26.4223 26.4223 33.25 18 33.25C9.57766 33.25 2.75 26.4223 2.75 18C2.75 9.57766 9.57766 2.75 18 2.75Z" fill="#1A1D25" stroke="#FC0000" />
                    <path d="M18 22.5C17.7016 22.5 17.4155 22.3815 17.2045 22.1705C16.9935 21.9595 16.875 21.6734 16.875 21.375V10.125C16.875 9.82663 16.9935 9.54048 17.2045 9.32951C17.4155 9.11853 17.7016 9 18 9C18.2984 9 18.5845 9.11853 18.7955 9.32951C19.0065 9.54048 19.125 9.82663 19.125 10.125V21.375C19.125 21.6734 19.0065 21.9595 18.7955 22.1705C18.5845 22.3815 18.2984 22.5 18 22.5Z" fill="#FC0000" />
                    <path d="M18 27C18.6213 27 19.125 26.4963 19.125 25.875C19.125 25.2537 18.6213 24.75 18 24.75C17.3787 24.75 16.875 25.2537 16.875 25.875C16.875 26.4963 17.3787 27 18 27Z" fill="#FC0000" />
                  </svg>
                </div>
                </NearMouseTooltip>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', 
                  // alignItems: 'center' 
                  }}>

                  <svg width="141" height="29" viewBox="0 0 141 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.4707 12H1.40625V1.29199H7.4707V2.77148H3.16406V5.6792H7.19971V7.15137H3.16406V10.5132H7.4707V12ZM13.7183 3.73828C13.855 3.73828 14.0039 3.74561 14.165 3.76025C14.3262 3.7749 14.4653 3.79443 14.5825 3.81885L14.4214 5.43018C14.3188 5.40088 14.1919 5.37891 14.0405 5.36426C13.894 5.34961 13.7622 5.34229 13.645 5.34229C13.3374 5.34229 13.0444 5.39355 12.7661 5.49609C12.4878 5.59375 12.2412 5.74512 12.0264 5.9502C11.8115 6.15039 11.6431 6.40186 11.521 6.70459C11.3989 7.00732 11.3379 7.35889 11.3379 7.75928V12H9.60938V3.89209H10.957L11.1914 5.32031H11.272C11.4331 5.03223 11.6333 4.76855 11.8726 4.5293C12.1118 4.29004 12.3853 4.09961 12.6929 3.95801C13.0054 3.81152 13.3472 3.73828 13.7183 3.73828ZM20.1929 3.73828C20.3296 3.73828 20.4785 3.74561 20.6396 3.76025C20.8008 3.7749 20.9399 3.79443 21.0571 3.81885L20.896 5.43018C20.7935 5.40088 20.6665 5.37891 20.5151 5.36426C20.3687 5.34961 20.2368 5.34229 20.1196 5.34229C19.812 5.34229 19.519 5.39355 19.2407 5.49609C18.9624 5.59375 18.7158 5.74512 18.501 5.9502C18.2861 6.15039 18.1177 6.40186 17.9956 6.70459C17.8735 7.00732 17.8125 7.35889 17.8125 7.75928V12H16.084V3.89209H17.4316L17.666 5.32031H17.7466C17.9077 5.03223 18.1079 4.76855 18.3472 4.5293C18.5864 4.29004 18.8599 4.09961 19.1675 3.95801C19.48 3.81152 19.8218 3.73828 20.1929 3.73828ZM29.7363 7.92773C29.7363 8.60156 29.6484 9.19971 29.4727 9.72217C29.2969 10.2446 29.0405 10.6865 28.7036 11.0479C28.3667 11.4043 27.9614 11.6777 27.4878 11.8682C27.0142 12.0537 26.4795 12.1465 25.8838 12.1465C25.3271 12.1465 24.8169 12.0537 24.353 11.8682C23.8892 11.6777 23.4863 11.4043 23.1445 11.0479C22.8076 10.6865 22.5464 10.2446 22.3608 9.72217C22.1753 9.19971 22.0825 8.60156 22.0825 7.92773C22.0825 7.03418 22.2363 6.27734 22.5439 5.65723C22.8564 5.03223 23.3008 4.55615 23.877 4.229C24.4531 3.90186 25.1392 3.73828 25.9351 3.73828C26.6821 3.73828 27.3413 3.90186 27.9126 4.229C28.4839 4.55615 28.9307 5.03223 29.2529 5.65723C29.5752 6.28223 29.7363 7.03906 29.7363 7.92773ZM23.855 7.92773C23.855 8.51855 23.9258 9.02393 24.0674 9.44385C24.2139 9.86377 24.4385 10.186 24.7412 10.4106C25.0439 10.6304 25.4346 10.7402 25.9131 10.7402C26.3916 10.7402 26.7822 10.6304 27.085 10.4106C27.3877 10.186 27.6099 9.86377 27.7515 9.44385C27.8931 9.02393 27.9639 8.51855 27.9639 7.92773C27.9639 7.33691 27.8931 6.83643 27.7515 6.42627C27.6099 6.01123 27.3877 5.69629 27.085 5.48145C26.7822 5.26172 26.3892 5.15186 25.9058 5.15186C25.1929 5.15186 24.6729 5.39111 24.3457 5.86963C24.0186 6.34814 23.855 7.03418 23.855 7.92773ZM35.8228 3.73828C35.9595 3.73828 36.1084 3.74561 36.2695 3.76025C36.4307 3.7749 36.5698 3.79443 36.687 3.81885L36.5259 5.43018C36.4233 5.40088 36.2964 5.37891 36.145 5.36426C35.9985 5.34961 35.8667 5.34229 35.7495 5.34229C35.4419 5.34229 35.1489 5.39355 34.8706 5.49609C34.5923 5.59375 34.3457 5.74512 34.1309 5.9502C33.916 6.15039 33.7476 6.40186 33.6255 6.70459C33.5034 7.00732 33.4424 7.35889 33.4424 7.75928V12H31.7139V3.89209H33.0615L33.2959 5.32031H33.3765C33.5376 5.03223 33.7378 4.76855 33.9771 4.5293C34.2163 4.29004 34.4897 4.09961 34.7974 3.95801C35.1099 3.81152 35.4517 3.73828 35.8228 3.73828ZM49.2627 7.92773C49.2627 8.60156 49.1748 9.19971 48.999 9.72217C48.8232 10.2446 48.5669 10.6865 48.23 11.0479C47.8931 11.4043 47.4878 11.6777 47.0142 11.8682C46.5405 12.0537 46.0059 12.1465 45.4102 12.1465C44.8535 12.1465 44.3433 12.0537 43.8794 11.8682C43.4155 11.6777 43.0127 11.4043 42.6709 11.0479C42.334 10.6865 42.0728 10.2446 41.8872 9.72217C41.7017 9.19971 41.6089 8.60156 41.6089 7.92773C41.6089 7.03418 41.7627 6.27734 42.0703 5.65723C42.3828 5.03223 42.8271 4.55615 43.4033 4.229C43.9795 3.90186 44.6655 3.73828 45.4614 3.73828C46.2085 3.73828 46.8677 3.90186 47.439 4.229C48.0103 4.55615 48.457 5.03223 48.7793 5.65723C49.1016 6.28223 49.2627 7.03906 49.2627 7.92773ZM43.3813 7.92773C43.3813 8.51855 43.4521 9.02393 43.5938 9.44385C43.7402 9.86377 43.9648 10.186 44.2676 10.4106C44.5703 10.6304 44.9609 10.7402 45.4395 10.7402C45.918 10.7402 46.3086 10.6304 46.6113 10.4106C46.9141 10.186 47.1362 9.86377 47.2778 9.44385C47.4194 9.02393 47.4902 8.51855 47.4902 7.92773C47.4902 7.33691 47.4194 6.83643 47.2778 6.42627C47.1362 6.01123 46.9141 5.69629 46.6113 5.48145C46.3086 5.26172 45.9155 5.15186 45.4321 5.15186C44.7192 5.15186 44.1992 5.39111 43.8721 5.86963C43.5449 6.34814 43.3813 7.03418 43.3813 7.92773ZM54.5068 12.1465C53.7402 12.1465 53.0762 11.9976 52.5146 11.6997C51.9531 11.4019 51.521 10.9453 51.2183 10.3301C50.9155 9.71484 50.7642 8.93359 50.7642 7.98633C50.7642 7 50.9302 6.19434 51.2622 5.56934C51.5942 4.94434 52.0532 4.48291 52.6392 4.18506C53.23 3.88721 53.9062 3.73828 54.668 3.73828C55.1514 3.73828 55.5884 3.78711 55.979 3.88477C56.3745 3.97754 56.709 4.09229 56.9824 4.229L56.4697 5.60596C56.1719 5.48389 55.8667 5.38135 55.5542 5.29834C55.2417 5.21533 54.9414 5.17383 54.6533 5.17383C54.1797 5.17383 53.7842 5.27881 53.4668 5.48877C53.1543 5.69873 52.9199 6.01123 52.7637 6.42627C52.6123 6.84131 52.5366 7.35645 52.5366 7.97168C52.5366 8.56738 52.6147 9.07031 52.771 9.48047C52.9272 9.88574 53.1592 10.1934 53.4668 10.4033C53.7744 10.6084 54.1528 10.7109 54.6021 10.7109C55.0464 10.7109 55.4443 10.6572 55.7959 10.5498C56.1475 10.4424 56.4795 10.3032 56.792 10.1323V11.6265C56.4844 11.8022 56.1548 11.9316 55.8032 12.0146C55.4517 12.1025 55.0195 12.1465 54.5068 12.1465ZM61.9629 12.1465C61.1963 12.1465 60.5322 11.9976 59.9707 11.6997C59.4092 11.4019 58.9771 10.9453 58.6743 10.3301C58.3716 9.71484 58.2202 8.93359 58.2202 7.98633C58.2202 7 58.3862 6.19434 58.7183 5.56934C59.0503 4.94434 59.5093 4.48291 60.0952 4.18506C60.686 3.88721 61.3623 3.73828 62.124 3.73828C62.6074 3.73828 63.0444 3.78711 63.4351 3.88477C63.8306 3.97754 64.165 4.09229 64.4385 4.229L63.9258 5.60596C63.6279 5.48389 63.3228 5.38135 63.0103 5.29834C62.6978 5.21533 62.3975 5.17383 62.1094 5.17383C61.6357 5.17383 61.2402 5.27881 60.9229 5.48877C60.6104 5.69873 60.376 6.01123 60.2197 6.42627C60.0684 6.84131 59.9927 7.35645 59.9927 7.97168C59.9927 8.56738 60.0708 9.07031 60.2271 9.48047C60.3833 9.88574 60.6152 10.1934 60.9229 10.4033C61.2305 10.6084 61.6089 10.7109 62.0581 10.7109C62.5024 10.7109 62.9004 10.6572 63.252 10.5498C63.6035 10.4424 63.9355 10.3032 64.248 10.1323V11.6265C63.9404 11.8022 63.6108 11.9316 63.2593 12.0146C62.9077 12.1025 62.4756 12.1465 61.9629 12.1465ZM73.2202 3.89209V12H71.8652L71.6309 10.9087H71.5356C71.3647 11.187 71.1475 11.4189 70.8838 11.6045C70.6201 11.7852 70.3271 11.9194 70.0049 12.0073C69.6826 12.1001 69.3433 12.1465 68.9868 12.1465C68.3765 12.1465 67.854 12.0439 67.4194 11.8389C66.9897 11.6289 66.6602 11.3066 66.4307 10.8721C66.2012 10.4375 66.0864 9.87598 66.0864 9.1875V3.89209H67.8149V8.86523C67.8149 9.49512 67.9419 9.96631 68.1958 10.2788C68.4546 10.5913 68.855 10.7476 69.397 10.7476C69.9194 10.7476 70.3345 10.6401 70.6421 10.4253C70.9497 10.2104 71.167 9.89307 71.2939 9.47314C71.4258 9.05322 71.4917 8.53809 71.4917 7.92773V3.89209H73.2202ZM79.7974 3.73828C79.9341 3.73828 80.083 3.74561 80.2441 3.76025C80.4053 3.7749 80.5444 3.79443 80.6616 3.81885L80.5005 5.43018C80.3979 5.40088 80.271 5.37891 80.1196 5.36426C79.9731 5.34961 79.8413 5.34229 79.7241 5.34229C79.4165 5.34229 79.1235 5.39355 78.8452 5.49609C78.5669 5.59375 78.3203 5.74512 78.1055 5.9502C77.8906 6.15039 77.7222 6.40186 77.6001 6.70459C77.478 7.00732 77.417 7.35889 77.417 7.75928V12H75.6885V3.89209H77.0361L77.2705 5.32031H77.3511C77.5122 5.03223 77.7124 4.76855 77.9517 4.5293C78.1909 4.29004 78.4644 4.09961 78.772 3.95801C79.0845 3.81152 79.4263 3.73828 79.7974 3.73828ZM86.272 3.73828C86.4087 3.73828 86.5576 3.74561 86.7188 3.76025C86.8799 3.7749 87.019 3.79443 87.1362 3.81885L86.9751 5.43018C86.8726 5.40088 86.7456 5.37891 86.5942 5.36426C86.4478 5.34961 86.3159 5.34229 86.1987 5.34229C85.8911 5.34229 85.5981 5.39355 85.3198 5.49609C85.0415 5.59375 84.7949 5.74512 84.5801 5.9502C84.3652 6.15039 84.1968 6.40186 84.0747 6.70459C83.9526 7.00732 83.8916 7.35889 83.8916 7.75928V12H82.1631V3.89209H83.5107L83.7451 5.32031H83.8257C83.9868 5.03223 84.187 4.76855 84.4263 4.5293C84.6655 4.29004 84.939 4.09961 85.2466 3.95801C85.5591 3.81152 85.9009 3.73828 86.272 3.73828ZM91.8677 3.73828C92.5903 3.73828 93.2104 3.88721 93.728 4.18506C94.2456 4.48291 94.6436 4.90527 94.9219 5.45215C95.2002 5.99902 95.3394 6.65332 95.3394 7.41504V8.33789H89.9268C89.9463 9.12402 90.1562 9.72949 90.5566 10.1543C90.9619 10.5791 91.5283 10.7915 92.2559 10.7915C92.7734 10.7915 93.2373 10.7427 93.6475 10.645C94.0625 10.5425 94.4897 10.3936 94.9292 10.1982V11.5972C94.5239 11.7876 94.1113 11.9268 93.6914 12.0146C93.2715 12.1025 92.7686 12.1465 92.1826 12.1465C91.3867 12.1465 90.686 11.9927 90.0806 11.6851C89.48 11.3726 89.0088 10.9087 88.667 10.2935C88.3301 9.67822 88.1616 8.91406 88.1616 8.00098C88.1616 7.09277 88.3154 6.32129 88.623 5.68652C88.9307 5.05176 89.3628 4.56836 89.9194 4.23633C90.4761 3.9043 91.1255 3.73828 91.8677 3.73828ZM91.8677 5.03467C91.3257 5.03467 90.8862 5.21045 90.5493 5.56201C90.2173 5.91357 90.022 6.42871 89.9634 7.10742H93.6548C93.6499 6.70215 93.5815 6.34326 93.4497 6.03076C93.3228 5.71826 93.1274 5.47412 92.8638 5.29834C92.605 5.12256 92.2729 5.03467 91.8677 5.03467ZM100.005 12.1465C99.0381 12.1465 98.2617 11.7949 97.6758 11.0918C97.0947 10.3838 96.8042 9.34131 96.8042 7.96436C96.8042 6.57275 97.0996 5.52051 97.6904 4.80762C98.2861 4.09473 99.0698 3.73828 100.042 3.73828C100.452 3.73828 100.811 3.79443 101.118 3.90674C101.426 4.01416 101.689 4.16064 101.909 4.34619C102.134 4.53174 102.324 4.73926 102.48 4.96875H102.561C102.537 4.81738 102.507 4.6001 102.473 4.31689C102.444 4.02881 102.429 3.7627 102.429 3.51855V0.603516H104.158V12H102.81L102.502 10.894H102.429C102.283 11.1284 102.097 11.3408 101.873 11.5312C101.653 11.7168 101.389 11.8657 101.082 11.978C100.779 12.0903 100.42 12.1465 100.005 12.1465ZM100.488 10.7476C101.226 10.7476 101.746 10.5352 102.048 10.1104C102.351 9.68555 102.507 9.04834 102.517 8.19873V7.97168C102.517 7.06348 102.371 6.36768 102.078 5.88428C101.785 5.396 101.25 5.15186 100.474 5.15186C99.8535 5.15186 99.3799 5.40332 99.0527 5.90625C98.7305 6.4043 98.5693 7.1001 98.5693 7.99365C98.5693 8.88721 98.7305 9.5708 99.0527 10.0444C99.3799 10.5132 99.8584 10.7476 100.488 10.7476Z" fill="#FC0000" />
                    <path d="M3.59766 28.1172C3.01953 28.1172 2.50977 27.998 2.06836 27.7598C1.62695 27.5215 1.2832 27.1582 1.03711 26.6699C0.791016 26.1816 0.667969 25.5664 0.667969 24.8242C0.667969 24.0469 0.796875 23.4121 1.05469 22.9199C1.31641 22.4238 1.67578 22.0566 2.13281 21.8184C2.58984 21.5801 3.10938 21.4609 3.69141 21.4609C4.01172 21.4609 4.32031 21.4941 4.61719 21.5605C4.91797 21.623 5.16406 21.7031 5.35547 21.8008L5.0625 22.6152C4.86719 22.5371 4.64062 22.4668 4.38281 22.4043C4.12891 22.3418 3.89062 22.3105 3.66797 22.3105C3.22266 22.3105 2.85156 22.4062 2.55469 22.5977C2.26172 22.7891 2.04102 23.0703 1.89258 23.4414C1.74805 23.8125 1.67578 24.2695 1.67578 24.8125C1.67578 25.332 1.74609 25.7754 1.88672 26.1426C2.03125 26.5098 2.24414 26.791 2.52539 26.9863C2.81055 27.1777 3.16602 27.2734 3.5918 27.2734C3.93164 27.2734 4.23828 27.2383 4.51172 27.168C4.78516 27.0938 5.0332 27.0078 5.25586 26.9102V27.7773C5.04102 27.8867 4.80078 27.9707 4.53516 28.0293C4.27344 28.0879 3.96094 28.1172 3.59766 28.1172ZM7.75781 28H6.7793V18.8828H7.75781V28ZM10.7871 21.5781V28H9.81445V21.5781H10.7871ZM10.3125 19.1758C10.4727 19.1758 10.6094 19.2285 10.7227 19.334C10.8398 19.4355 10.8984 19.5957 10.8984 19.8145C10.8984 20.0293 10.8398 20.1895 10.7227 20.2949C10.6094 20.4004 10.4727 20.4531 10.3125 20.4531C10.1445 20.4531 10.0039 20.4004 9.89062 20.2949C9.78125 20.1895 9.72656 20.0293 9.72656 19.8145C9.72656 19.5957 9.78125 19.4355 9.89062 19.334C10.0039 19.2285 10.1445 19.1758 10.3125 19.1758ZM15.4219 28.1172C14.8438 28.1172 14.334 27.998 13.8926 27.7598C13.4512 27.5215 13.1074 27.1582 12.8613 26.6699C12.6152 26.1816 12.4922 25.5664 12.4922 24.8242C12.4922 24.0469 12.6211 23.4121 12.8789 22.9199C13.1406 22.4238 13.5 22.0566 13.957 21.8184C14.4141 21.5801 14.9336 21.4609 15.5156 21.4609C15.8359 21.4609 16.1445 21.4941 16.4414 21.5605C16.7422 21.623 16.9883 21.7031 17.1797 21.8008L16.8867 22.6152C16.6914 22.5371 16.4648 22.4668 16.207 22.4043C15.9531 22.3418 15.7148 22.3105 15.4922 22.3105C15.0469 22.3105 14.6758 22.4062 14.3789 22.5977C14.0859 22.7891 13.8652 23.0703 13.7168 23.4414C13.5723 23.8125 13.5 24.2695 13.5 24.8125C13.5 25.332 13.5703 25.7754 13.7109 26.1426C13.8555 26.5098 14.0684 26.791 14.3496 26.9863C14.6348 27.1777 14.9902 27.2734 15.416 27.2734C15.7559 27.2734 16.0625 27.2383 16.3359 27.168C16.6094 27.0938 16.8574 27.0078 17.0801 26.9102V27.7773C16.8652 27.8867 16.625 27.9707 16.3594 28.0293C16.0977 28.0879 15.7852 28.1172 15.4219 28.1172ZM19.5703 18.8828V23.6172C19.5703 23.7734 19.5645 23.9648 19.5527 24.1914C19.541 24.4141 19.5312 24.6113 19.5234 24.7832H19.5645C19.6465 24.6816 19.7656 24.5312 19.9219 24.332C20.082 24.1328 20.2168 23.9727 20.3262 23.8516L22.459 21.5781H23.6016L21.0176 24.3203L23.7891 28H22.6172L20.3496 24.959L19.5703 25.6738V28H18.6035V18.8828H19.5703ZM33.5625 24.7773C33.5625 25.3047 33.4941 25.7754 33.3574 26.1895C33.2207 26.6035 33.0234 26.9531 32.7656 27.2383C32.5078 27.5234 32.1953 27.7422 31.8281 27.8945C31.4648 28.043 31.0527 28.1172 30.5918 28.1172C30.1621 28.1172 29.7676 28.043 29.4082 27.8945C29.0527 27.7422 28.7441 27.5234 28.4824 27.2383C28.2246 26.9531 28.0234 26.6035 27.8789 26.1895C27.7383 25.7754 27.668 25.3047 27.668 24.7773C27.668 24.0742 27.7871 23.4766 28.0254 22.9844C28.2637 22.4883 28.6035 22.1113 29.0449 21.8535C29.4902 21.5918 30.0195 21.4609 30.6328 21.4609C31.2188 21.4609 31.7305 21.5918 32.168 21.8535C32.6094 22.1152 32.9512 22.4941 33.1934 22.9902C33.4395 23.4824 33.5625 24.0781 33.5625 24.7773ZM28.6758 24.7773C28.6758 25.293 28.7441 25.7402 28.8809 26.1191C29.0176 26.498 29.2285 26.791 29.5137 26.998C29.7988 27.2051 30.166 27.3086 30.6152 27.3086C31.0605 27.3086 31.4258 27.2051 31.7109 26.998C32 26.791 32.2129 26.498 32.3496 26.1191C32.4863 25.7402 32.5547 25.293 32.5547 24.7773C32.5547 24.2656 32.4863 23.8242 32.3496 23.4531C32.2129 23.0781 32.002 22.7891 31.7168 22.5859C31.4316 22.3828 31.0625 22.2812 30.6094 22.2812C29.9414 22.2812 29.4512 22.502 29.1387 22.9434C28.8301 23.3848 28.6758 23.9961 28.6758 24.7773ZM38.2852 21.4609C39.0469 21.4609 39.623 21.6484 40.0137 22.0234C40.4043 22.3945 40.5996 22.9922 40.5996 23.8164V28H39.6387V23.8809C39.6387 23.3457 39.5156 22.9453 39.2695 22.6797C39.0273 22.4141 38.6543 22.2812 38.1504 22.2812C37.4395 22.2812 36.9395 22.4824 36.6504 22.8848C36.3613 23.2871 36.2168 23.8711 36.2168 24.6367V28H35.2441V21.5781H36.0293L36.1758 22.5039H36.2285C36.3652 22.2773 36.5391 22.0879 36.75 21.9355C36.9609 21.7793 37.1973 21.6621 37.459 21.584C37.7207 21.502 37.9961 21.4609 38.2852 21.4609ZM48.1816 19.4336C48.8848 19.4336 49.4648 19.5215 49.9219 19.6973C50.3828 19.8691 50.7266 20.1328 50.9531 20.4883C51.1797 20.8438 51.293 21.2969 51.293 21.8477C51.293 22.3008 51.2109 22.6797 51.0469 22.9844C50.8828 23.2852 50.668 23.5293 50.4023 23.7168C50.1367 23.9043 49.8535 24.0488 49.5527 24.1504L51.9023 28H50.7422L48.6445 24.4258H46.8633V28H45.8672V19.4336H48.1816ZM48.123 20.2949H46.8633V23.582H48.2227C48.9219 23.582 49.4355 23.4395 49.7637 23.1543C50.0957 22.8691 50.2617 22.4492 50.2617 21.8945C50.2617 21.3125 50.0859 20.9004 49.7344 20.6582C49.3867 20.416 48.8496 20.2949 48.123 20.2949ZM55.5996 21.4609C56.1465 21.4609 56.6152 21.582 57.0059 21.8242C57.3965 22.0664 57.6953 22.4062 57.9023 22.8438C58.1094 23.2773 58.2129 23.7852 58.2129 24.3672V24.9707H53.7773C53.7891 25.7246 53.9766 26.2988 54.3398 26.6934C54.7031 27.0879 55.2148 27.2852 55.875 27.2852C56.2812 27.2852 56.6406 27.248 56.9531 27.1738C57.2656 27.0996 57.5898 26.9902 57.9258 26.8457V27.7012C57.6016 27.8457 57.2793 27.9512 56.959 28.0176C56.6426 28.084 56.2676 28.1172 55.834 28.1172C55.2168 28.1172 54.6777 27.9922 54.2168 27.7422C53.7598 27.4883 53.4043 27.1172 53.1504 26.6289C52.8965 26.1406 52.7695 25.543 52.7695 24.8359C52.7695 24.1445 52.8848 23.5469 53.1152 23.043C53.3496 22.5352 53.6777 22.1445 54.0996 21.8711C54.5254 21.5977 55.0254 21.4609 55.5996 21.4609ZM55.5879 22.2578C55.0684 22.2578 54.6543 22.4277 54.3457 22.7676C54.0371 23.1074 53.8535 23.582 53.7949 24.1914H57.1934C57.1895 23.8086 57.1289 23.4727 57.0117 23.1836C56.8984 22.8906 56.7246 22.6641 56.4902 22.5039C56.2559 22.3398 55.9551 22.2578 55.5879 22.2578ZM62.7246 22.3398H61.125V28H60.1523V22.3398H59.0156V21.8711L60.1523 21.5488V21.1211C60.1523 20.582 60.2324 20.1445 60.3926 19.8086C60.5527 19.4688 60.7852 19.2188 61.0898 19.0586C61.3945 18.8984 61.7656 18.8184 62.2031 18.8184C62.4492 18.8184 62.6738 18.8398 62.877 18.8828C63.084 18.9258 63.2656 18.9746 63.4219 19.0293L63.1699 19.7969C63.0371 19.7539 62.8867 19.7148 62.7188 19.6797C62.5547 19.6406 62.3867 19.6211 62.2148 19.6211C61.8438 19.6211 61.5684 19.7422 61.3887 19.9844C61.2129 20.2227 61.125 20.5977 61.125 21.1094V21.5781H62.7246V22.3398ZM66.8262 21.4609C66.9551 21.4609 67.0898 21.4688 67.2305 21.4844C67.3711 21.4961 67.4961 21.5137 67.6055 21.5371L67.4824 22.4395C67.377 22.4121 67.2598 22.3906 67.1309 22.375C67.002 22.3594 66.8809 22.3516 66.7676 22.3516C66.5098 22.3516 66.2656 22.4043 66.0352 22.5098C65.8086 22.6113 65.6094 22.7598 65.4375 22.9551C65.2656 23.1465 65.1309 23.3789 65.0332 23.6523C64.9355 23.9219 64.8867 24.2227 64.8867 24.5547V28H63.9082V21.5781H64.7168L64.8223 22.7617H64.8633C64.9961 22.5234 65.1562 22.3066 65.3438 22.1113C65.5312 21.9121 65.748 21.7539 65.9941 21.6367C66.2441 21.5195 66.5215 21.4609 66.8262 21.4609ZM71.291 21.4609C71.8379 21.4609 72.3066 21.582 72.6973 21.8242C73.0879 22.0664 73.3867 22.4062 73.5938 22.8438C73.8008 23.2773 73.9043 23.7852 73.9043 24.3672V24.9707H69.4688C69.4805 25.7246 69.668 26.2988 70.0312 26.6934C70.3945 27.0879 70.9062 27.2852 71.5664 27.2852C71.9727 27.2852 72.332 27.248 72.6445 27.1738C72.957 27.0996 73.2812 26.9902 73.6172 26.8457V27.7012C73.293 27.8457 72.9707 27.9512 72.6504 28.0176C72.334 28.084 71.959 28.1172 71.5254 28.1172C70.9082 28.1172 70.3691 27.9922 69.9082 27.7422C69.4512 27.4883 69.0957 27.1172 68.8418 26.6289C68.5879 26.1406 68.4609 25.543 68.4609 24.8359C68.4609 24.1445 68.5762 23.5469 68.8066 23.043C69.041 22.5352 69.3691 22.1445 69.791 21.8711C70.2168 21.5977 70.7168 21.4609 71.291 21.4609ZM71.2793 22.2578C70.7598 22.2578 70.3457 22.4277 70.0371 22.7676C69.7285 23.1074 69.5449 23.582 69.4863 24.1914H72.8848C72.8809 23.8086 72.8203 23.4727 72.7031 23.1836C72.5898 22.8906 72.416 22.6641 72.1816 22.5039C71.9473 22.3398 71.6465 22.2578 71.2793 22.2578ZM79.7109 26.2422C79.7109 26.6523 79.6074 26.998 79.4004 27.2793C79.1973 27.5566 78.9043 27.7656 78.5215 27.9062C78.1426 28.0469 77.6895 28.1172 77.1621 28.1172C76.7129 28.1172 76.3242 28.082 75.9961 28.0117C75.668 27.9414 75.3809 27.8418 75.1348 27.7129V26.8164C75.3965 26.9453 75.709 27.0625 76.0723 27.168C76.4355 27.2734 76.8066 27.3262 77.1855 27.3262C77.7402 27.3262 78.1426 27.2363 78.3926 27.0566C78.6426 26.877 78.7676 26.6328 78.7676 26.3242C78.7676 26.1484 78.7168 25.9941 78.6152 25.8613C78.5176 25.7246 78.3516 25.5938 78.1172 25.4688C77.8828 25.3398 77.5586 25.1992 77.1445 25.0469C76.7344 24.8906 76.3789 24.7363 76.0781 24.584C75.7812 24.4277 75.5508 24.2383 75.3867 24.0156C75.2266 23.793 75.1465 23.5039 75.1465 23.1484C75.1465 22.6055 75.3652 22.1895 75.8027 21.9004C76.2441 21.6074 76.8223 21.4609 77.5371 21.4609C77.9238 21.4609 78.2852 21.5 78.6211 21.5781C78.9609 21.6523 79.2773 21.7539 79.5703 21.8828L79.2422 22.6621C78.9766 22.5488 78.6934 22.4531 78.3926 22.375C78.0918 22.2969 77.7852 22.2578 77.4727 22.2578C77.0234 22.2578 76.6777 22.332 76.4355 22.4805C76.1973 22.6289 76.0781 22.832 76.0781 23.0898C76.0781 23.2891 76.1328 23.4531 76.2422 23.582C76.3555 23.7109 76.5352 23.832 76.7812 23.9453C77.0273 24.0586 77.3516 24.1914 77.7539 24.3438C78.1562 24.4922 78.5039 24.6465 78.7969 24.8066C79.0898 24.9629 79.3145 25.1543 79.4707 25.3809C79.6309 25.6035 79.7109 25.8906 79.7109 26.2422ZM82.248 18.8828V21.6074C82.248 21.7637 82.2441 21.9219 82.2363 22.082C82.2285 22.2383 82.2148 22.3828 82.1953 22.5156H82.2598C82.3926 22.2891 82.5605 22.0996 82.7637 21.9473C82.9707 21.791 83.2051 21.6738 83.4668 21.5957C83.7285 21.5137 84.0059 21.4727 84.2988 21.4727C84.8145 21.4727 85.2441 21.5547 85.5879 21.7188C85.9355 21.8828 86.1953 22.1367 86.3672 22.4805C86.543 22.8242 86.6309 23.2695 86.6309 23.8164V28H85.6699V23.8809C85.6699 23.3457 85.5469 22.9453 85.3008 22.6797C85.0586 22.4141 84.6855 22.2812 84.1816 22.2812C83.7051 22.2812 83.3242 22.373 83.0391 22.5566C82.7578 22.7363 82.5547 23.002 82.4297 23.3535C82.3086 23.7051 82.248 24.1348 82.248 24.6426V28H81.2754V18.8828H82.248ZM93.8262 27.3203C93.9863 27.3203 94.1504 27.3066 94.3184 27.2793C94.4863 27.252 94.623 27.2188 94.7285 27.1797V27.9355C94.6152 27.9863 94.457 28.0293 94.2539 28.0645C94.0547 28.0996 93.8594 28.1172 93.668 28.1172C93.3281 28.1172 93.0195 28.0586 92.7422 27.9414C92.4648 27.8203 92.2422 27.6172 92.0742 27.332C91.9102 27.0469 91.8281 26.6523 91.8281 26.1484V22.3398H90.9141V21.8652L91.834 21.4844L92.2207 20.0898H92.8066V21.5781H94.6875V22.3398H92.8066V26.1191C92.8066 26.5215 92.8984 26.8223 93.082 27.0215C93.2695 27.2207 93.5176 27.3203 93.8262 27.3203ZM101.566 24.7773C101.566 25.3047 101.498 25.7754 101.361 26.1895C101.225 26.6035 101.027 26.9531 100.77 27.2383C100.512 27.5234 100.199 27.7422 99.832 27.8945C99.4688 28.043 99.0566 28.1172 98.5957 28.1172C98.166 28.1172 97.7715 28.043 97.4121 27.8945C97.0566 27.7422 96.748 27.5234 96.4863 27.2383C96.2285 26.9531 96.0273 26.6035 95.8828 26.1895C95.7422 25.7754 95.6719 25.3047 95.6719 24.7773C95.6719 24.0742 95.791 23.4766 96.0293 22.9844C96.2676 22.4883 96.6074 22.1113 97.0488 21.8535C97.4941 21.5918 98.0234 21.4609 98.6367 21.4609C99.2227 21.4609 99.7344 21.5918 100.172 21.8535C100.613 22.1152 100.955 22.4941 101.197 22.9902C101.443 23.4824 101.566 24.0781 101.566 24.7773ZM96.6797 24.7773C96.6797 25.293 96.748 25.7402 96.8848 26.1191C97.0215 26.498 97.2324 26.791 97.5176 26.998C97.8027 27.2051 98.1699 27.3086 98.6191 27.3086C99.0645 27.3086 99.4297 27.2051 99.7148 26.998C100.004 26.791 100.217 26.498 100.354 26.1191C100.49 25.7402 100.559 25.293 100.559 24.7773C100.559 24.2656 100.49 23.8242 100.354 23.4531C100.217 23.0781 100.006 22.7891 99.7207 22.5859C99.4355 22.3828 99.0664 22.2812 98.6133 22.2812C97.9453 22.2812 97.4551 22.502 97.1426 22.9434C96.834 23.3848 96.6797 23.9961 96.6797 24.7773ZM109.283 21.4609C109.412 21.4609 109.547 21.4688 109.688 21.4844C109.828 21.4961 109.953 21.5137 110.062 21.5371L109.939 22.4395C109.834 22.4121 109.717 22.3906 109.588 22.375C109.459 22.3594 109.338 22.3516 109.225 22.3516C108.967 22.3516 108.723 22.4043 108.492 22.5098C108.266 22.6113 108.066 22.7598 107.895 22.9551C107.723 23.1465 107.588 23.3789 107.49 23.6523C107.393 23.9219 107.344 24.2227 107.344 24.5547V28H106.365V21.5781H107.174L107.279 22.7617H107.32C107.453 22.5234 107.613 22.3066 107.801 22.1113C107.988 21.9121 108.205 21.7539 108.451 21.6367C108.701 21.5195 108.979 21.4609 109.283 21.4609ZM113.748 21.4609C114.295 21.4609 114.764 21.582 115.154 21.8242C115.545 22.0664 115.844 22.4062 116.051 22.8438C116.258 23.2773 116.361 23.7852 116.361 24.3672V24.9707H111.926C111.938 25.7246 112.125 26.2988 112.488 26.6934C112.852 27.0879 113.363 27.2852 114.023 27.2852C114.43 27.2852 114.789 27.248 115.102 27.1738C115.414 27.0996 115.738 26.9902 116.074 26.8457V27.7012C115.75 27.8457 115.428 27.9512 115.107 28.0176C114.791 28.084 114.416 28.1172 113.982 28.1172C113.365 28.1172 112.826 27.9922 112.365 27.7422C111.908 27.4883 111.553 27.1172 111.299 26.6289C111.045 26.1406 110.918 25.543 110.918 24.8359C110.918 24.1445 111.033 23.5469 111.264 23.043C111.498 22.5352 111.826 22.1445 112.248 21.8711C112.674 21.5977 113.174 21.4609 113.748 21.4609ZM113.736 22.2578C113.217 22.2578 112.803 22.4277 112.494 22.7676C112.186 23.1074 112.002 23.582 111.943 24.1914H115.342C115.338 23.8086 115.277 23.4727 115.16 23.1836C115.047 22.8906 114.873 22.6641 114.639 22.5039C114.404 22.3398 114.104 22.2578 113.736 22.2578ZM118.992 28H118.014V18.8828H118.992V28ZM126.586 24.7773C126.586 25.3047 126.518 25.7754 126.381 26.1895C126.244 26.6035 126.047 26.9531 125.789 27.2383C125.531 27.5234 125.219 27.7422 124.852 27.8945C124.488 28.043 124.076 28.1172 123.615 28.1172C123.186 28.1172 122.791 28.043 122.432 27.8945C122.076 27.7422 121.768 27.5234 121.506 27.2383C121.248 26.9531 121.047 26.6035 120.902 26.1895C120.762 25.7754 120.691 25.3047 120.691 24.7773C120.691 24.0742 120.811 23.4766 121.049 22.9844C121.287 22.4883 121.627 22.1113 122.068 21.8535C122.514 21.5918 123.043 21.4609 123.656 21.4609C124.242 21.4609 124.754 21.5918 125.191 21.8535C125.633 22.1152 125.975 22.4941 126.217 22.9902C126.463 23.4824 126.586 24.0781 126.586 24.7773ZM121.699 24.7773C121.699 25.293 121.768 25.7402 121.904 26.1191C122.041 26.498 122.252 26.791 122.537 26.998C122.822 27.2051 123.189 27.3086 123.639 27.3086C124.084 27.3086 124.449 27.2051 124.734 26.998C125.023 26.791 125.236 26.498 125.373 26.1191C125.51 25.7402 125.578 25.293 125.578 24.7773C125.578 24.2656 125.51 23.8242 125.373 23.4531C125.236 23.0781 125.025 22.7891 124.74 22.5859C124.455 22.3828 124.086 22.2812 123.633 22.2812C122.965 22.2812 122.475 22.502 122.162 22.9434C121.854 23.3848 121.699 23.9961 121.699 24.7773ZM130.67 21.4727C131.436 21.4727 132.004 21.6445 132.375 21.9883C132.746 22.332 132.932 22.8809 132.932 23.6348V28H132.223L132.035 27.0508H131.988C131.809 27.2852 131.621 27.4824 131.426 27.6426C131.23 27.7988 131.004 27.918 130.746 28C130.492 28.0781 130.18 28.1172 129.809 28.1172C129.418 28.1172 129.07 28.0488 128.766 27.9121C128.465 27.7754 128.227 27.5684 128.051 27.291C127.879 27.0137 127.793 26.6621 127.793 26.2363C127.793 25.5957 128.047 25.1035 128.555 24.7598C129.062 24.416 129.836 24.2285 130.875 24.1973L131.982 24.1504V23.7578C131.982 23.2031 131.863 22.8145 131.625 22.5918C131.387 22.3691 131.051 22.2578 130.617 22.2578C130.281 22.2578 129.961 22.3066 129.656 22.4043C129.352 22.502 129.062 22.6172 128.789 22.75L128.49 22.0117C128.779 21.8633 129.111 21.7363 129.486 21.6309C129.861 21.5254 130.256 21.4727 130.67 21.4727ZM131.971 24.8359L130.992 24.877C130.191 24.9082 129.627 25.0391 129.299 25.2695C128.971 25.5 128.807 25.8262 128.807 26.248C128.807 26.6152 128.918 26.8867 129.141 27.0625C129.363 27.2383 129.658 27.3262 130.025 27.3262C130.596 27.3262 131.062 27.168 131.426 26.8516C131.789 26.5352 131.971 26.0605 131.971 25.4277V24.8359ZM137.221 28.1172C136.408 28.1172 135.764 27.8398 135.287 27.2852C134.814 26.7305 134.578 25.9062 134.578 24.8125C134.578 23.707 134.82 22.873 135.305 22.3105C135.789 21.7441 136.434 21.4609 137.238 21.4609C137.578 21.4609 137.875 21.5059 138.129 21.5957C138.383 21.6855 138.602 21.8066 138.785 21.959C138.969 22.1074 139.123 22.2773 139.248 22.4688H139.318C139.303 22.3477 139.287 22.1816 139.271 21.9707C139.256 21.7598 139.248 21.5879 139.248 21.4551V18.8828H140.221V28H139.436L139.289 27.0859H139.248C139.127 27.2773 138.973 27.4512 138.785 27.6074C138.602 27.7637 138.381 27.8887 138.123 27.9824C137.869 28.0723 137.568 28.1172 137.221 28.1172ZM137.373 27.3086C138.061 27.3086 138.547 27.1133 138.832 26.7227C139.117 26.332 139.26 25.752 139.26 24.9824V24.8066C139.26 23.9902 139.123 23.3633 138.85 22.9258C138.58 22.4883 138.088 22.2695 137.373 22.2695C136.775 22.2695 136.328 22.5 136.031 22.9609C135.734 23.418 135.586 24.043 135.586 24.8359C135.586 25.625 135.732 26.2344 136.025 26.6641C136.322 27.0938 136.771 27.3086 137.373 27.3086Z" fill="#F5F5F5" />
                  </svg>
                  
                    {/* Error Message Below SVG */}
  {errorMessage && (
    <div
      style={{
        marginTop: '4px',
        color: '#FF4D4F',
        fontSize: '10px',
        // textAlign: 'left',
      }}
    >
      {errorMessage}
    </div>
  )}
                </div>
<div style={{display:'flex',flexDirection:'column',justifyContent:'center',marginRight:'12px'}}>

  <div style={{display:'flex',justifyContent:'flex-end'}}>
    <svg width="60" height="14" viewBox="0 0 60 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M57.5181 11.4746H58.0553V12.4953L58.8074 11.4746H59.452L58.6999 12.4953L59.6132 13.9995H58.9685L58.3776 12.9788L58.0553 13.3549V13.9995H57.5181V11.4746Z" fill="#F58220"/>
<path d="M41.7241 11.4746H42.4225C43.1746 11.4746 43.6581 11.8507 43.6581 12.7102C43.6581 13.5697 43.1746 13.9458 42.4225 13.9458H41.6704L41.7241 11.4746ZM42.4225 13.516C42.8523 13.516 43.1209 13.3011 43.1209 12.7102C43.1209 12.1193 42.7986 11.9044 42.4225 11.9044H42.3151V13.4623L42.4225 13.516Z" fill="#F5F5F5"/>
<path d="M44.625 11.4746H46.1829V11.9581H45.1622V12.4416H46.0218V12.9251H45.1622V13.4623H46.1829V13.9458H44.5713L44.625 11.4746Z" fill="#F5F5F5"/>
<path d="M47.2034 11.4746H48.7613V11.9581H47.7406V12.4416H48.6001V12.9251H47.7406V13.4623H48.7613V13.9458H47.1497L47.2034 11.4746Z" fill="#F5F5F5"/>
<path d="M49.7283 11.4746H50.5878C51.125 11.4746 51.5548 11.6358 51.5548 12.2804C51.5548 12.9251 51.0713 13.0863 50.5878 13.0863H50.2655V13.9458H49.7283V11.4746ZM50.5878 12.7102C50.9101 12.7102 51.0176 12.549 51.0176 12.2804C51.0176 12.0118 50.8564 11.9044 50.5341 11.9044H50.2655V12.6565L50.5878 12.7102Z" fill="#F5F5F5"/>
<path d="M52.8981 11.9581H52.2534V11.4746H54.1874V11.9581H53.5427V13.9458H53.0055L52.8981 11.9581Z" fill="#F58220"/>
<path d="M54.9929 11.4746H56.5509V11.9581H55.5301V12.4416H56.3897V12.9251H55.5301V13.4623H56.5509V13.9458H54.9392L54.9929 11.4746Z" fill="#F58220"/>
<path d="M15.6154 5.78132C15.6154 5.7276 15.6154 5.67388 15.6154 5.62015C15.6691 4.76061 15.7766 3.90107 16.4212 3.25641C17.0121 2.66547 17.9254 2.45059 18.7312 2.45059C19.5371 2.39687 20.3966 2.50431 21.2024 2.50431L21.2561 7.87645C21.2561 8.78972 21.2561 9.7567 20.7189 10.5088C20.2354 11.1535 19.5371 11.5295 18.7312 11.5832C17.9791 11.637 17.227 11.5295 16.5824 11.2072C16.5287 11.2072 16.4749 11.1535 16.4749 11.1535C16.4749 11.1535 16.5287 11.0997 16.5287 11.046L16.7435 10.8311L17.1733 10.4014C17.227 10.3476 17.227 10.3476 17.2808 10.3476C17.3345 10.2939 17.4419 10.2939 17.4419 10.3476C17.818 10.4551 18.194 10.5088 18.5701 10.4014C18.9461 10.2939 19.2685 10.1328 19.4833 9.86415C19.6982 9.43438 19.8057 8.95088 19.8057 8.46739C19.0536 9.11205 17.9254 9.32693 17.0659 8.89716C16.5287 8.57483 16.0989 8.09134 15.9377 7.5004C15.7228 6.90947 15.5617 6.31853 15.6154 5.78132ZM17.0121 5.94248C16.9584 6.4797 17.1196 7.01691 17.3882 7.44668C17.6031 7.82273 18.0329 7.9839 18.4626 7.9839C18.9998 8.03762 19.5371 7.66157 19.7519 7.17808V4.43828C19.5371 3.95479 19.0536 3.68618 18.4626 3.68618C18.0329 3.68618 17.6568 3.84735 17.3882 4.2234C17.1196 4.76061 17.0121 5.35155 17.0121 5.94248Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
<path d="M45.0546 2.44922C46.3976 2.44922 47.0423 3.25504 47.096 4.75924V9.00323H45.6455V4.86668C45.6993 4.54435 45.5918 4.22203 45.3769 3.95342C45.162 3.73853 44.8397 3.63109 44.5174 3.63109C43.9802 3.63109 43.443 3.95342 43.2281 4.43691V9.00323H41.7776L41.8313 2.50294C41.8313 2.50294 44.2488 2.44922 45.0546 2.44922Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
<path d="M24.6943 2.55795L24.7481 3.20261C25.2316 2.66539 25.8762 2.39678 26.6283 2.39678C27.4879 2.39678 28.0788 2.71911 28.4011 3.41749C28.8309 2.77283 29.583 2.39678 30.3888 2.39678C30.9798 2.34306 31.517 2.55795 31.9467 2.98772C32.3228 3.47121 32.484 4.11587 32.484 4.70681V8.89708H31.0335V4.81425C31.0335 4.49192 30.926 4.16959 30.7111 3.95471C30.4425 3.73982 30.1739 3.63238 29.7979 3.63238C29.4756 3.63238 29.2069 3.6861 28.9921 3.90099C28.7772 4.06215 28.616 4.33076 28.5623 4.59936V8.9508H27.1655V4.76053C27.2193 4.22331 26.7895 3.6861 26.2523 3.63238C26.1985 3.63238 26.0911 3.63238 26.0374 3.63238C25.5002 3.57866 25.0167 3.90098 24.8018 4.33076V8.9508H23.405V2.55795H24.6943Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
<path d="M37.3724 9.1116C36.5665 9.16532 35.7607 8.84299 35.1698 8.25206C34.5788 7.6074 34.2565 6.80158 34.3102 5.94204V5.72715C34.3102 5.13621 34.4177 4.54528 34.6863 4.00806C34.9012 3.52457 35.2772 3.0948 35.707 2.82619C36.1368 2.50386 36.674 2.39642 37.2112 2.39642C38.017 2.3427 38.7154 2.66503 39.2526 3.25596C39.7898 3.90062 40.0584 4.76016 40.0047 5.61971V6.21064H35.7607C35.7607 6.69414 35.9219 7.12391 36.2979 7.44624C36.6203 7.76856 37.05 7.92973 37.4798 7.87601C38.1245 7.87601 38.7154 7.55368 39.0914 7.07019L39.8973 7.82229C39.6287 8.19834 39.2526 8.52066 38.8766 8.73555C38.3393 9.00416 37.8559 9.1116 37.3724 9.1116ZM37.1575 3.57829C36.7814 3.57829 36.4591 3.73946 36.1905 4.00806C35.9219 4.33039 35.7607 4.76016 35.7607 5.13621H38.5005V5.02877C38.5005 4.599 38.3931 4.22295 38.1245 3.95434C37.8559 3.68573 37.5335 3.57829 37.1575 3.57829Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
<path d="M51.0715 1V2.55792H52.1997V3.63235H51.0715V7.23169C51.0715 7.39285 51.1253 7.60774 51.2327 7.7689C51.3939 7.87634 51.555 7.93006 51.7699 7.93006C51.9311 7.93006 52.0922 7.87634 52.3071 7.87634V9.00449C51.9848 9.05822 51.6625 9.16566 51.3401 9.16566C50.212 9.16566 49.6748 8.57472 49.6748 7.33913V3.63235H48.6003V2.55792H49.6748V1H51.0715Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
<path d="M53.6501 5.72812C53.6501 5.13718 53.7575 4.54624 54.0262 4.00903C54.241 3.52554 54.6171 3.09577 55.0469 2.88088C55.5304 2.61227 56.0676 2.45111 56.6048 2.45111C57.4106 2.39739 58.1627 2.71972 58.6999 3.31065C59.2371 3.90159 59.6132 4.70741 59.6132 5.51323V5.83556C59.6132 6.4265 59.5057 6.96371 59.2371 7.55465C59.0222 8.03814 58.6462 8.46791 58.2164 8.6828C57.7329 8.9514 57.1957 9.11257 56.6585 9.11257C55.8527 9.16629 55.0469 8.79024 54.4559 8.1993C53.9187 7.55465 53.5964 6.6951 53.6501 5.78184V5.72812ZM55.1006 5.83556C55.0469 6.37277 55.208 6.90999 55.5304 7.39348C56.0138 8.03814 56.9271 8.14558 57.518 7.71581C57.6255 7.66209 57.6792 7.55465 57.8404 7.39348C58.1627 6.90999 58.2701 6.31905 58.2701 5.72812C58.3239 5.1909 58.1627 4.59997 57.8404 4.17019C57.4106 3.52554 56.4973 3.41809 55.8527 3.84787C55.7452 3.90159 55.6378 4.00903 55.5304 4.17019C55.208 4.65369 55.0469 5.24462 55.1006 5.83556Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
<path d="M12.4457 8.62908C12.0159 9.16629 11.3176 9.4349 10.6192 9.38118C10.0282 9.4349 9.43731 9.22001 9.00754 8.79024C8.63149 8.25303 8.41661 7.66209 8.47033 7.01743V2.77344H9.92081V6.90999C9.92081 7.71581 10.2431 8.14558 10.9415 8.14558C11.6399 8.14558 12.1234 7.87697 12.3383 7.39348V2.77344H13.7887V9.16629H12.392L12.4457 8.62908Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
<path d="M5.1934 9.05887C5.13968 8.84398 5.03224 8.6291 5.03224 8.41421C4.60247 8.8977 3.95781 9.16631 3.31315 9.16631C2.72222 9.22003 2.13128 9.00515 1.70151 8.6291C1.21802 8.25305 1.00313 7.71583 1.00313 7.17862C0.949409 6.58768 1.27174 5.94302 1.80895 5.56697C2.45361 5.1372 3.25943 4.92232 4.06525 5.02976H4.97852V4.54627C4.97852 4.22394 4.9248 3.95533 4.65619 3.74045C4.4413 3.47184 4.11897 3.3644 3.79665 3.41812C3.47432 3.41812 3.20571 3.47184 2.9371 3.68672C2.77594 3.79417 2.61477 4.06277 2.61477 4.27766H1.16429C1.16429 3.90161 1.32546 3.52556 1.54034 3.25695C1.80895 2.93462 2.13128 2.66602 2.50733 2.50485C2.9371 2.39741 3.4206 2.23625 3.90409 2.28997C4.54875 2.23625 5.24712 2.50485 5.73062 2.8809C6.21411 3.31067 6.48272 3.90161 6.429 4.54627V7.55467C6.37527 8.03816 6.48272 8.52165 6.6976 9.00515V9.11259L5.1934 9.05887ZM3.58176 7.98444C3.90409 7.98444 4.1727 7.93072 4.38758 7.76955C4.65619 7.60839 4.81735 7.44722 4.97852 7.17862V5.8893H4.1727C3.6892 5.83558 3.31315 5.99675 2.88338 6.21163C2.61477 6.3728 2.45361 6.69512 2.45361 7.01745C2.45361 7.28606 2.50733 7.55467 2.77594 7.71583C2.99082 7.877 3.25943 7.98444 3.58176 7.98444Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
</svg>

  </div>
  <div style={{paddingTop:'8px'}}>
    <RefreshButtonErrorScreen
       onClick={() => window.location.reload()}
    />
  </div>
</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  }

  if (appState === "loading") {
    return (<div
      id="gizmo-widget-root"
      ref={containerRef}
      style={{ width: '358px', marginTop: '5px', height: '46px',padding: '0px 0px 8px 8px' }}
    >
      <div className="gizmo-container" ref={containerRef2}>
        <div
          ref={containerRef1}
          style={{
            width: '100%',
            height: '100%',
            background: '#040811',
            marginTop:'10px'
          }}
        >
          <div
            ref={containerRef1}
            className="gizmo-container"
            style={{
              width: '350px',
              justifyContent: 'center',
              background:
                activePanel === null && animationComplete === true
                  ? 'transparent'
                  : 'rgb(12, 26, 44)', // ✅ FIXED spelling
              borderBottomLeftRadius: 50,
              borderTopLeftRadius: activePanel === null ? 50 : 0,
              transition: 'border-top-left-radius 1s ease',

              // borderBottom: '0.3px solid rgba(245, 245, 245, 0.20)',
              // borderRight: '0.3px solid rgba(245, 245, 245, 0.20)',
              borderLeft: '0.3px solid rgba(245, 245, 245, 0.4)',
              borderBottomRightRadius: 10,


            }}
          >
            <div style={{
              width: '100%',  background: '#040811', zIndex: 20,
              height: '46px',
              // boxShadow: '0px 0px 10px rgba(255, 255, 255, 0.50)',
              borderRadius: '10px',
              border: '0.30px rgba(245, 245, 245, 0.20) solid',
              boxShadow: `
                  4px 0 7px rgba(255, 255, 255, 0.25),   /* right */
                -4px 0 7px rgba(255, 255, 255, 0.25),   /* left */
                  0 4px 7px rgba(255, 255, 255, 0.25)    /* bottom */
                `,
              display:'flex',alignItems:'center'
            }} >
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' ,width:'350px',alignItems:'center'}} >
                
                <div style={{display: 'flex',alignItems:'center',marginLeft:'29px'}}>
                  <img
                    src={`${window.location.origin}/images/loading.gif`} // ✅ Replace with actual path or URL
                    alt="loading"
                    style={{
                      width: '29px',     // or whatever size you need
                      height: '29px',
                      objectFit: 'contain'
                    }}
                  />
                </div>
                <div style={{
                  display: 'flex',
                  alignItems:'center',
                  marginRight:'70px', 
                  fontStyle: 'normal',
                  fontWeight: '600',
                  fontSize: '15px',
                  lineHeight: '20px',
                  color: '#C4C4C4',}}>
                  Loading....

                </div>
<div style={{display:'flex',flexDirection:'column',justifyContent:'center',marginRight:'12px',marginBottom:'30px', marginTop: '12px'}}>

  
    <svg width="60" height="14" viewBox="0 0 60 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M57.5181 11.4746H58.0553V12.4953L58.8074 11.4746H59.452L58.6999 12.4953L59.6132 13.9995H58.9685L58.3776 12.9788L58.0553 13.3549V13.9995H57.5181V11.4746Z" fill="#F58220"/>
<path d="M41.7241 11.4746H42.4225C43.1746 11.4746 43.6581 11.8507 43.6581 12.7102C43.6581 13.5697 43.1746 13.9458 42.4225 13.9458H41.6704L41.7241 11.4746ZM42.4225 13.516C42.8523 13.516 43.1209 13.3011 43.1209 12.7102C43.1209 12.1193 42.7986 11.9044 42.4225 11.9044H42.3151V13.4623L42.4225 13.516Z" fill="#F5F5F5"/>
<path d="M44.625 11.4746H46.1829V11.9581H45.1622V12.4416H46.0218V12.9251H45.1622V13.4623H46.1829V13.9458H44.5713L44.625 11.4746Z" fill="#F5F5F5"/>
<path d="M47.2034 11.4746H48.7613V11.9581H47.7406V12.4416H48.6001V12.9251H47.7406V13.4623H48.7613V13.9458H47.1497L47.2034 11.4746Z" fill="#F5F5F5"/>
<path d="M49.7283 11.4746H50.5878C51.125 11.4746 51.5548 11.6358 51.5548 12.2804C51.5548 12.9251 51.0713 13.0863 50.5878 13.0863H50.2655V13.9458H49.7283V11.4746ZM50.5878 12.7102C50.9101 12.7102 51.0176 12.549 51.0176 12.2804C51.0176 12.0118 50.8564 11.9044 50.5341 11.9044H50.2655V12.6565L50.5878 12.7102Z" fill="#F5F5F5"/>
<path d="M52.8981 11.9581H52.2534V11.4746H54.1874V11.9581H53.5427V13.9458H53.0055L52.8981 11.9581Z" fill="#F58220"/>
<path d="M54.9929 11.4746H56.5509V11.9581H55.5301V12.4416H56.3897V12.9251H55.5301V13.4623H56.5509V13.9458H54.9392L54.9929 11.4746Z" fill="#F58220"/>
<path d="M15.6154 5.78132C15.6154 5.7276 15.6154 5.67388 15.6154 5.62015C15.6691 4.76061 15.7766 3.90107 16.4212 3.25641C17.0121 2.66547 17.9254 2.45059 18.7312 2.45059C19.5371 2.39687 20.3966 2.50431 21.2024 2.50431L21.2561 7.87645C21.2561 8.78972 21.2561 9.7567 20.7189 10.5088C20.2354 11.1535 19.5371 11.5295 18.7312 11.5832C17.9791 11.637 17.227 11.5295 16.5824 11.2072C16.5287 11.2072 16.4749 11.1535 16.4749 11.1535C16.4749 11.1535 16.5287 11.0997 16.5287 11.046L16.7435 10.8311L17.1733 10.4014C17.227 10.3476 17.227 10.3476 17.2808 10.3476C17.3345 10.2939 17.4419 10.2939 17.4419 10.3476C17.818 10.4551 18.194 10.5088 18.5701 10.4014C18.9461 10.2939 19.2685 10.1328 19.4833 9.86415C19.6982 9.43438 19.8057 8.95088 19.8057 8.46739C19.0536 9.11205 17.9254 9.32693 17.0659 8.89716C16.5287 8.57483 16.0989 8.09134 15.9377 7.5004C15.7228 6.90947 15.5617 6.31853 15.6154 5.78132ZM17.0121 5.94248C16.9584 6.4797 17.1196 7.01691 17.3882 7.44668C17.6031 7.82273 18.0329 7.9839 18.4626 7.9839C18.9998 8.03762 19.5371 7.66157 19.7519 7.17808V4.43828C19.5371 3.95479 19.0536 3.68618 18.4626 3.68618C18.0329 3.68618 17.6568 3.84735 17.3882 4.2234C17.1196 4.76061 17.0121 5.35155 17.0121 5.94248Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
<path d="M45.0546 2.44922C46.3976 2.44922 47.0423 3.25504 47.096 4.75924V9.00323H45.6455V4.86668C45.6993 4.54435 45.5918 4.22203 45.3769 3.95342C45.162 3.73853 44.8397 3.63109 44.5174 3.63109C43.9802 3.63109 43.443 3.95342 43.2281 4.43691V9.00323H41.7776L41.8313 2.50294C41.8313 2.50294 44.2488 2.44922 45.0546 2.44922Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
<path d="M24.6943 2.55795L24.7481 3.20261C25.2316 2.66539 25.8762 2.39678 26.6283 2.39678C27.4879 2.39678 28.0788 2.71911 28.4011 3.41749C28.8309 2.77283 29.583 2.39678 30.3888 2.39678C30.9798 2.34306 31.517 2.55795 31.9467 2.98772C32.3228 3.47121 32.484 4.11587 32.484 4.70681V8.89708H31.0335V4.81425C31.0335 4.49192 30.926 4.16959 30.7111 3.95471C30.4425 3.73982 30.1739 3.63238 29.7979 3.63238C29.4756 3.63238 29.2069 3.6861 28.9921 3.90099C28.7772 4.06215 28.616 4.33076 28.5623 4.59936V8.9508H27.1655V4.76053C27.2193 4.22331 26.7895 3.6861 26.2523 3.63238C26.1985 3.63238 26.0911 3.63238 26.0374 3.63238C25.5002 3.57866 25.0167 3.90098 24.8018 4.33076V8.9508H23.405V2.55795H24.6943Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
<path d="M37.3724 9.1116C36.5665 9.16532 35.7607 8.84299 35.1698 8.25206C34.5788 7.6074 34.2565 6.80158 34.3102 5.94204V5.72715C34.3102 5.13621 34.4177 4.54528 34.6863 4.00806C34.9012 3.52457 35.2772 3.0948 35.707 2.82619C36.1368 2.50386 36.674 2.39642 37.2112 2.39642C38.017 2.3427 38.7154 2.66503 39.2526 3.25596C39.7898 3.90062 40.0584 4.76016 40.0047 5.61971V6.21064H35.7607C35.7607 6.69414 35.9219 7.12391 36.2979 7.44624C36.6203 7.76856 37.05 7.92973 37.4798 7.87601C38.1245 7.87601 38.7154 7.55368 39.0914 7.07019L39.8973 7.82229C39.6287 8.19834 39.2526 8.52066 38.8766 8.73555C38.3393 9.00416 37.8559 9.1116 37.3724 9.1116ZM37.1575 3.57829C36.7814 3.57829 36.4591 3.73946 36.1905 4.00806C35.9219 4.33039 35.7607 4.76016 35.7607 5.13621H38.5005V5.02877C38.5005 4.599 38.3931 4.22295 38.1245 3.95434C37.8559 3.68573 37.5335 3.57829 37.1575 3.57829Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
<path d="M51.0715 1V2.55792H52.1997V3.63235H51.0715V7.23169C51.0715 7.39285 51.1253 7.60774 51.2327 7.7689C51.3939 7.87634 51.555 7.93006 51.7699 7.93006C51.9311 7.93006 52.0922 7.87634 52.3071 7.87634V9.00449C51.9848 9.05822 51.6625 9.16566 51.3401 9.16566C50.212 9.16566 49.6748 8.57472 49.6748 7.33913V3.63235H48.6003V2.55792H49.6748V1H51.0715Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
<path d="M53.6501 5.72812C53.6501 5.13718 53.7575 4.54624 54.0262 4.00903C54.241 3.52554 54.6171 3.09577 55.0469 2.88088C55.5304 2.61227 56.0676 2.45111 56.6048 2.45111C57.4106 2.39739 58.1627 2.71972 58.6999 3.31065C59.2371 3.90159 59.6132 4.70741 59.6132 5.51323V5.83556C59.6132 6.4265 59.5057 6.96371 59.2371 7.55465C59.0222 8.03814 58.6462 8.46791 58.2164 8.6828C57.7329 8.9514 57.1957 9.11257 56.6585 9.11257C55.8527 9.16629 55.0469 8.79024 54.4559 8.1993C53.9187 7.55465 53.5964 6.6951 53.6501 5.78184V5.72812ZM55.1006 5.83556C55.0469 6.37277 55.208 6.90999 55.5304 7.39348C56.0138 8.03814 56.9271 8.14558 57.518 7.71581C57.6255 7.66209 57.6792 7.55465 57.8404 7.39348C58.1627 6.90999 58.2701 6.31905 58.2701 5.72812C58.3239 5.1909 58.1627 4.59997 57.8404 4.17019C57.4106 3.52554 56.4973 3.41809 55.8527 3.84787C55.7452 3.90159 55.6378 4.00903 55.5304 4.17019C55.208 4.65369 55.0469 5.24462 55.1006 5.83556Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
<path d="M12.4457 8.62908C12.0159 9.16629 11.3176 9.4349 10.6192 9.38118C10.0282 9.4349 9.43731 9.22001 9.00754 8.79024C8.63149 8.25303 8.41661 7.66209 8.47033 7.01743V2.77344H9.92081V6.90999C9.92081 7.71581 10.2431 8.14558 10.9415 8.14558C11.6399 8.14558 12.1234 7.87697 12.3383 7.39348V2.77344H13.7887V9.16629H12.392L12.4457 8.62908Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
<path d="M5.1934 9.05887C5.13968 8.84398 5.03224 8.6291 5.03224 8.41421C4.60247 8.8977 3.95781 9.16631 3.31315 9.16631C2.72222 9.22003 2.13128 9.00515 1.70151 8.6291C1.21802 8.25305 1.00313 7.71583 1.00313 7.17862C0.949409 6.58768 1.27174 5.94302 1.80895 5.56697C2.45361 5.1372 3.25943 4.92232 4.06525 5.02976H4.97852V4.54627C4.97852 4.22394 4.9248 3.95533 4.65619 3.74045C4.4413 3.47184 4.11897 3.3644 3.79665 3.41812C3.47432 3.41812 3.20571 3.47184 2.9371 3.68672C2.77594 3.79417 2.61477 4.06277 2.61477 4.27766H1.16429C1.16429 3.90161 1.32546 3.52556 1.54034 3.25695C1.80895 2.93462 2.13128 2.66602 2.50733 2.50485C2.9371 2.39741 3.4206 2.23625 3.90409 2.28997C4.54875 2.23625 5.24712 2.50485 5.73062 2.8809C6.21411 3.31067 6.48272 3.90161 6.429 4.54627V7.55467C6.37527 8.03816 6.48272 8.52165 6.6976 9.00515V9.11259L5.1934 9.05887ZM3.58176 7.98444C3.90409 7.98444 4.1727 7.93072 4.38758 7.76955C4.65619 7.60839 4.81735 7.44722 4.97852 7.17862V5.8893H4.1727C3.6892 5.83558 3.31315 5.99675 2.88338 6.21163C2.61477 6.3728 2.45361 6.69512 2.45361 7.01745C2.45361 7.28606 2.50733 7.55467 2.77594 7.71583C2.99082 7.877 3.25943 7.98444 3.58176 7.98444Z" fill="#F5F5F5" stroke="#F5F5F5" stroke-width="0.5" stroke-miterlimit="10"/>
</svg>
</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>);
  }

  if (appState === "ready") {
    return (
      <div
        id="gizmo-widget-root"
        ref={containerRef}
        style={{ display: "inline-block", padding: '0px 0px 8px 8px' }}
      >
        <div className="gizmo-container" ref={containerRef2}>

          <div className="gizmo-main"
            style={{
              borderRadius: activePanel === null && animationComplete === true ? '50px 10px 10px 50px' : '10px 10px 10px 50px',
            }}
          >
            <> <div className="gizmo-widget-wrapper" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>

              <div
                className={`gizmo-panel-wrapper ${activePanel !== null ? 'open' : ''}`}
              >
                <AnimatePresence
                  mode="wait"
                  initial={false}
                  onExitComplete={() => {
                    setAnimationComplete(true);

                    measureAndResize({
                      activePanel: pendingPanel, // this is the panel we're going to show next
                      containerRef,
                      containerRef1,
                      containerRef2,
                      postResizeIfChanged
                    });
                  }}

                >
                  {(activePanel === 'findings' || activePanel === 'next' || activePanel === 'analytics' || activePanel === 'rai') && (
                    <motion.div
                      key={`panel-${activePanel}`}
                      initial={skipAnimationRef.current ? false : { opacity: 0, y: 10, scale: 0.95 }}
                      animate={skipAnimationRef.current ? {} : { opacity: 1, y: 0, scale: 1 }}
                      exit={skipAnimationRef.current ? {} : { opacity: 0, y: 10, scale: 0.95 }}
                      transition={skipAnimationRef.current ? { duration: 0 } : { duration: 1, ease: [0.25, 0.8, 0.25, 1] }}

                      onAnimationComplete={() => {

                        setAnimationComplete(false);
                      }}
                      style={{
                        overflow: "hidden",
                        width: '350px',
                        height: '246px',
                        pointerEvents: 'auto',
                        borderRadius: '10px 10px 0px 0px',
                        transformOrigin: 'top center',
                        zIndex: 0,
                        background: '#0C1A2C',
                        borderTop: '0.3px solid rgba(245, 245, 245, 0.20)',
                        borderRight: '0.3px solid rgba(245, 245, 245, 0.20)',
                        borderLeft: '0.3px solid rgba(245, 245, 245, 0.20)',
                        boxShadow: `
                        4px 0 7px rgba(255, 255, 255, 0.25),    /* right */
                      -4px 0 7px rgba(255, 255, 255, 0.25),    /* left */
                        0 -4px 7px rgba(255, 255, 255, 0.25)    /* top */
                      `,

                      }}
                    >

                      <div className="gizmo-panel">
                        {activePanel === 'findings' && (
                          <AIAnnotatedImage
                            images={data.images}
                            isChestXray={data.isChestXray}
                            studyId={studyId}
                            firmId={firmId}
                            userName={userName}
                            findingsAvailable={findingsAvailable}
                            findings={false ? data.studyClassificationPredictionList : data.studyPredictionList}
                            showProbability={!false && getEnv("REACT_APP_PROTECTED_MODE") === 'false'}
                            setShowFindings={() => {
                              setActivePanel(null);
                              setShowPlaceholder(true);
                            }}
                            aiModel={data.aiVendor}
                            patientName={data.patientName}
                            patientSex={data.patientSex}
                            patientAge={data.patientAge}
                            aiOutput={data.aiOutput}
                            isAugDemo={isAugDemo}
                            augDemoStudyId={augDemoStudyId}
                            augDemoFirmId={augDemoFirmId}
                            augDemoUsername={augDemoUsername}
                            scSeries={data.scSeries}
                          />
                        )}

                        {activePanel === 'next' && (
                          <NextStudiesTable
                            studies={nextStudies}
                            userName={userName}
                            setShowNextStudies={() => {
                              setActivePanel(null);
                              setShowPlaceholder(true);
                            }}
                            setSomePredictionId={setSomePredictionId}
                          />
                        )}

                        {activePanel === 'analytics' && (
                          <AnalyticsPage
                            normality="total" // or "abnormal" / "total"
                            chartType="radiologist_tat_trend"
                            setShowAnalyticsPanel={(show) => {
                              setActivePanel(null);
                              setShowPlaceholder(!show);
                            }}
                            studyId={studyId}
                            firmId={firmId}
                            userName={userName}
                            modalityName={modalityName}
                          />
                        )}

                        {activePanel === 'rai' && (
                          <RAIPerformanceResults
                            data={data}
                            studyId={studyId}
                            firmId={firmId}
                            userName={userName}
                            findings={findings}
                            setShowRAIPanel={(show) => {
                              setActivePanel(null);
                              setShowPlaceholder(!show);
                            }}
                            setErrorMessage={setErrorMessage}
                          />
                        )}

                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>


              <div
                ref={containerRef1}
                className="gizmo-container"
                style={{
                  width: '350px',
                  height: '46px',
                  background:
                    activePanel === null && animationComplete === true
                      ? 'transparent'
                      : 'rgb(12, 26, 44)', // ✅ FIXED spelling
                  borderBottomLeftRadius: 50,
                  borderTopLeftRadius: activePanel === null ? 50 : 0,
                  transition: 'border-top-left-radius 1s ease',

                  // borderBottom: '0.3px solid rgba(245, 245, 245, 0.20)',
                  // borderRight: '0.3px solid rgba(245, 245, 245, 0.20)',
                  borderLeft: '0.3px solid rgba(245, 245, 245, 0.4)',
                  borderBottomRightRadius: 10,


                }}
              >
                <div style={{
                  width: '100%', height: '100%', background: '#040811', zIndex: 20,
                  // boxShadow: '0px 0px 10px rgba(255, 255, 255, 0.50)',
                  borderTopLeftRadius: activePanel === null ? 10 : 0,
                  borderTopRightRadius: activePanel === null ? 10 : 0,
                  borderBottomRightRadius: 10, borderBottomLeftRadius: 10,
                  border: '0.30px rgba(245, 245, 245, 0.20) solid',
                  boxShadow: `
                  4px 0 7px rgba(255, 255, 255, 0.25),   /* right */
                -4px 0 7px rgba(255, 255, 255, 0.25),   /* left */
                  0 4px 7px rgba(255, 255, 255, 0.25)    /* bottom */
                `

                }} >

                  <div className="gizmo-container" style={{ alignItems: 'center', height: '100%'}}>
                    <AIDetailsButton
                      onClick={() => {
                        setColapseView(false);
                        switchPanel('findings');
                      }}
                      isOpen={activePanel === 'findings'}
                      icon={aiEmegencyDetails.icon}
                      label={aiEmegencyDetails.label}
                      patientName={data.patientName}
                      patientSex={data.patientSex}
                      patientAge={data.patientAge}
                      aiModel={data.aiVendor}
                      aiFindings={
                        data.isChestXray
                          ? (
                            false
                              ? (data.studyClassificationPredictionList.length
                                ? data.studyClassificationPredictionList.map(item => item.classificationName).join(', ')
                                : 'Normal')
                              : (data.studyPredictionList.length
                                ? data.studyPredictionList.map(item => item.pathologyName).join(', ')
                                : 'Normal')
                          )
                          : (
                            data.aiOutput
                              ? data.aiOutput
                                .split(',')
                                .map(item => item.split('-')[0])  // Take only the part before the dash
                                .join(', ')
                              : 'Normal'
                          )
                      }

                    />

                    <div style={{ width: '154px', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-evenly' }}>
                      <ReportButtonIcon
                        onClick={() => {
                          if(data.augmentoDemoStudyJson){
                            handleOpenViewerWindow(augDemoStudyId, augDemoFirmId, augDemoUsername, true,true,false,"");
                          }
                          else{
                            handleOpenViewerWindow2(studyId, firmId, userName, true,false,false,"");
                          }
                          
                        }}
                      />
                      <RAIIcon
                        onClick={() => {
                          setColapseView(false);
                          switchPanel('rai');
                        }}
                        isOpen={activePanel === 'rai'}
                        // disabled={(raiAuthStatus?.result && raiAuthStatus.result === 'FAILURE') ? true : false}
                      />
                      <RadilogistAnalytics
                        onClick={() => {
                          setColapseView(false);
                          switchPanel('analytics');
                        }}
                        isOpen={activePanel === 'analytics'}
                      />

                      <NextStudiesIcon
                        onClick={() => {
                          setColapseView(false);
                          switchPanel('next');
                          handleOpenStudyList();
                        }}
                        disabled={nextStudies?.length === 0}
                        highlight={highlightNextStudies}
                        isOpen={activePanel === 'next'}
                      />
                    </div>
                  </div>
                </div>

              </div>
            </div>
            </>


          </div>

        </div>
      </div>
    );
  }
};

export default GizmoWidget;
